import { clientStorage } from "@/lib/clientStorage";
import { isDemoMode, INTAKE_EMAIL } from "@/lib/demoConfig";
import React, { useState, useEffect, useCallback } from "react";
import LacwWordmark from "@/components/LacwWordmark";
import ShareDraftButtons from "@/components/ShareDraftButtons";
import CloudDraftsPanel from "@/components/CloudDraftsPanel";
import { createPageUrl } from "@/utils";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogFooter, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { 
  ArrowLeft, Printer, RotateCcw, Building2, User, FileText, Plus, Trash2, CheckCircle2, Eye, Mail, 
  Phone, AlertTriangle, ShieldCheck, Heart, DollarSign, Calendar, ListTodo, Award, FileEdit,
  Paperclip, Upload, BriefcaseBusiness, ChevronRight,
  RefreshCw, ExternalLink, ChevronLeft
} from "lucide-react";
import { toast } from "sonner";
import { base44 } from "@/api/base44Client";
import AddressAutocomplete from "@/components/form/AddressAutocomplete";
import policeStationsData from "@/data/all_vic_police_stations.json";
import { getAppearanceTypesForCourt } from "@/lib/vla-fees-data";
import ActionstepSavePanel from "@/components/ActionstepSavePanel";
import { OFFENCE_CLASSIFICATION, OFFENCE_HEADINGS, withoutOffenceCode } from "@/lib/offenceClassification";

const COURT_LOCATIONS_BASE = [
  "Ararat",
  "Bacchus Marsh",
  "Bairnsdale",
  "Ballarat",
  "Benalla",
  "Bendigo",
  "Broadmeadows",
  "Castlemaine",
  "Cobram",
  "Colac",
  "Corryong",
  "Dandenong",
  "Dromana",
  "Echuca",
  "Edenhope",
  "Frankston",
  "Geelong",
  "Hamilton",
  "Heidelberg",
  "Hopetoun",
  "Horsham",
  "Kerang",
  "Korumburra",
  "Kyneton",
  "Latrobe Valley",
  "Mansfield",
  "Maryborough",
  "Melbourne",
  "Mildura",
  "Moorabbin Justice Centre",
  "Myrtleford",
  "Neighbourhood Justice Centre",
  "Nhill",
  "Omeo",
  "Orbost",
  "Ouyen",
  "Portland",
  "Ringwood",
  "Robinvale",
  "Sale",
  "Seymour",
  "Shepparton",
  "St Arnaud",
  "Stawell",
  "Sunshine",
  "Swan Hill",
  "Wangaratta",
  "Warrnambool",
  "Werribee",
  "Wodonga",
  "Wonthaggi"
];

const mapIntakeCourtToVlaCourt = (courtType) => {
  switch (courtType) {
    case "Children’s":
      return "Children's Court";
    case "Magistrates'":
      return "Magistrates Court";
    case "County":
      return "County Court";
    case "Supreme":
      return "Supreme Court";
    default:
      return "Other";
  }
};

const getCourtLocationOptions = (courtType) => {
  if (!courtType) return [];
  
  return COURT_LOCATIONS_BASE.map(loc => {
    if (loc === "Neighbourhood Justice Centre") {
      return "Neighbourhood Justice Centre";
    }
    return loc.replace(" Justice Centre", "");
  });
};

const getHearingTypeOptions = (courtType) => {
  const vlaCourtName = mapIntakeCourtToVlaCourt(courtType);
  const appearanceTypes = getAppearanceTypesForCourt(vlaCourtName);
  return appearanceTypes.map(at => {
    const label = at.label;
    // Strip "fee" / "fees" (case-insensitive)
    let cleaned = label
      .replace(/\bfees\b/gi, "")
      .replace(/\bfee\b/gi, "")
      .replace(/\s+/g, " ")
      .trim();
    // Clean up trailing dashes or spaces
    cleaned = cleaned.replace(/\s*[–-]\s*$/, "").trim();
    cleaned = cleaned.replace(/\s*–\s*–\s*/g, " – ").trim();
    return cleaned || label;
  });
};

const COURT_REGISTRY_EMAILS = {
  "Ararat": "araratcoordinator@courts.vic.gov.au",
  "Bacchus Marsh": "Bacchusmarshcoordinator@courts.vic.gov.au",
  "Bairnsdale": "bairnsdalecoordinator@courts.vic.gov.au",
  "Ballarat": "ballaratcoordinator@courts.vic.gov.au",
  "Benalla": "benallacoordinator@courts.vic.gov.au",
  "Bendigo": "bendigocoordinator@courts.vic.gov.au",
  "Broadmeadows": "broadmeadowscoordinator@courts.vic.gov.au",
  "Castlemaine": "castlemainecoordinator@courts.vic.gov.au",
  "Cobram": "cobramcoordinator@courts.vic.gov.au",
  "Colac": "colac@courts.vic.gov.au",
  "Corryong": "corryongcoordinator@courts.vic.gov.au",
  "Dandenong": "dandenongcoordinator@courts.vic.gov.au",
  "Dromana": "dromanacoordinator@courts.vic.gov.au",
  "Echuca": "echucacoordinator@courts.vic.gov.au",
  "Edenhope": "horshamcoordinator@courts.vic.gov.au",
  "Frankston": "frankstoncoordinator@courts.vic.gov.au",
  "Geelong": "geelong@courts.vic.gov.au",
  "Hamilton": "hamilton@courts.vic.gov.au",
  "Heidelberg": "heidelbergcoordinator@courts.vic.gov.au",
  "Hopetoun": "horshamcoordinator@courts.vic.gov.au",
  "Horsham": "horshamcoordinator@courts.vic.gov.au",
  "Kerang": "kerangcoordinator@courts.vic.gov.au",
  "Korumburra": "korumburracoordinator@courts.vic.gov.au",
  "Kyneton": "kynetoncoordinator@courts.vic.gov.au",
  "Latrobe Valley": "lvcoordinator@courts.vic.gov.au",
  "Mansfield": "mansfieldcoordinator@courts.vic.gov.au",
  "Maryborough": "maryboroughcoordinator@courts.vic.gov.au",
  "Melbourne": "criminalcoordinator@courts.vic.gov.au",
  "Mildura": "milduracoordinator@courts.vic.gov.au",
  "Moorabbin": "moorabbincoordinator@courts.vic.gov.au",
  "Myrtleford": "myrtlefordcoordinator@courts.vic.gov.au",
  "Neighbourhood Justice Centre": "njccoordinator@courts.vic.gov.au",
  "Nhill": "horshamcoordinator@courts.vic.gov.au",
  "Orbost": "bairnsdalecoordinator@courts.vic.gov.au",
  "Ouyen": "milduracoordinator@courts.vic.gov.au",
  "Portland": "portland@courts.vic.gov.au",
  "Robinvale": "milduracoordinator@courts.vic.gov.au",
  "Sale": "salecoordinator@courts.vic.gov.au",
  "Seymour": "seymourcoordinator@courts.vic.gov.au",
  "Shepparton": "sheppartoncoordinator@courts.vic.gov.au",
  "St Arnaud": "starnaudcoordinator@courts.vic.gov.au",
  "Stawell": "stawellcoordinator@courts.vic.gov.au",
  "Sunshine": "sunshinecoordinator@courts.vic.gov.au",
  "Swan Hill": "swanhillcoordinator@courts.vic.gov.au",
  "Wangaratta": "wangarattacoordinator@courts.vic.gov.au",
  "Warrnambool": "warrnambool@courts.vic.gov.au",
  "Wodonga": "wodongacoordinator@courts.vic.gov.au",
  "Wonthaggi": "wonthaggicoordinator@courts.vic.gov.au"
};

const getCourtCoordinatorEmail = (location, courtType) => {
  if (!location) return "";
  
  if (courtType === "Children’s") {
    return "childrenscourt@courts.vic.gov.au";
  }
  if (courtType === "County") {
    return "criminal.registry@countycourt.vic.gov.au";
  }
  if (courtType === "Supreme") {
    return "criminaldivision@supremecourt.vic.gov.au";
  }
  
  const cleanLoc = location.trim();
  if (COURT_REGISTRY_EMAILS[cleanLoc]) {
    return COURT_REGISTRY_EMAILS[cleanLoc];
  }
  
  // Specific aliases or mappings
  if (cleanLoc === "Werribee") {
    return "wyndhamcoordinator@courts.vic.gov.au";
  }
  if (cleanLoc === "Omeo") {
    return "bairnsdalecoordinator@courts.vic.gov.au";
  }
  
  // Default fallback
  const cleanSlug = cleanLoc.toLowerCase().replace(/\s+/g, "").replace(/[^a-z0-9]/g, "");
  return `mcv-${cleanSlug}@courts.vic.gov.au`;
};

const generateDefaultEmailSubject = (data) => {
  const clientName = `${data.first_name || ""} ${data.surname || ""}`.trim() || "Client";
  const caseId = data.charges_list?.[0]?.vp_number || data.crn || "TBA";
  let dateStr = "TBA";
  if (data.court_date) {
    const parts = data.court_date.split("-");
    if (parts.length === 3) {
      dateStr = `${parts[2]}/${parts[1]}/${parts[0]}`;
    }
  }
  return `Adjournment Request - ${clientName} - ${caseId} - ${dateStr}`;
};

const generateDefaultEmailBody = (data) => {
  return "";
};

const parseAddressString = (addressStr) => {
  if (!addressStr) return { street: "", suburb: "", postcode: "" };

  const parts = addressStr.split(",").map(p => p.trim());
  if (parts.length <= 1) {
    return { street: addressStr, suburb: "", postcode: "" };
  }

  let postcode = "";
  let postcodeIndex = -1;
  for (let i = 0; i < parts.length; i++) {
    if (/^\d{4}$/.test(parts[i])) {
      postcode = parts[i];
      postcodeIndex = i;
      break;
    }
  }

  let street = parts[0];
  let suburb = "";

  if (postcodeIndex !== -1) {
    if (postcodeIndex > 1) {
      suburb = parts[1];
    } else if (postcodeIndex === 1) {
      suburb = parts[0];
    }
  } else {
    if (parts.length >= 3) {
      suburb = parts[1];
    } else if (parts.length === 2) {
      suburb = parts[1];
    }
  }

  return { street, suburb, postcode };
};

const CUSTODY_LOCATIONS_DETAILS = {
  // Prisons
  "Barwon Prison (Lara)": {
    address: "1140 Bacchus Marsh Road, Lara VIC 3212",
    phone: "(03) 5220 8222",
    email: "barwon.prison@corrections.vic.gov.au"
  },
  "Melbourne Assessment Prison (Melbourne)": {
    address: "317-353 Spencer Street, West Melbourne VIC 3003",
    phone: "(03) 9321 4111",
    email: "map.prison@corrections.vic.gov.au"
  },
  "Metropolitan Remand Centre (Ravenhall)": {
    address: "Middle Road, Ravenhall VIC 3023",
    phone: "(03) 9217 7777",
    email: "mrc.prison@corrections.vic.gov.au"
  },
  "Port Phillip Prison (Truganina)": {
    address: "451 Dohertys Road, Truganina VIC 3029",
    phone: "(03) 9217 7200",
    email: "portphillip.prison@corrections.vic.gov.au"
  },
  "Ravenhall Correctional Centre (Ravenhall)": {
    address: "97 Riding Boundary Road, Ravenhall VIC 3023",
    phone: "(03) 8363 2650",
    email: "ravenhall.prison@corrections.vic.gov.au"
  },
  "Fulham Correctional Centre (Fulham)": {
    address: "110 Hopkins Road, Fulham VIC 3851",
    phone: "(03) 5142 3800",
    email: "fulham.prison@corrections.vic.gov.au"
  },
  "Dame Phyllis Frost Centre (Ravenhall)": {
    address: "101-201 Riding Boundary Road, Ravenhall VIC 3023",
    phone: "(03) 9217 8400",
    email: "dpfc.prison@corrections.vic.gov.au"
  },
  "Loddon Prison (Castlemaine)": {
    address: "19 Hitchcock Street, Castlemaine VIC 3450",
    phone: "(03) 5471 1200",
    email: "loddon.prison@corrections.vic.gov.au"
  },
  "Hopkins Correctional Centre (Ararat)": {
    address: "156A Warrak Road, Ararat VIC 3377",
    phone: "(03) 5352 8200",
    email: "hopkins.prison@corrections.vic.gov.au"
  },
  "Langi Kal Kal Prison (Langi Kal Kal)": {
    address: "Langi Kal Kal Road, Langi Kal Kal VIC 3352",
    phone: "(03) 5349 4700",
    email: "langikalkal.prison@corrections.vic.gov.au"
  },
  "Beechworth Correctional Centre (Beechworth)": {
    address: "494 Flat Rock Road, Beechworth VIC 3747",
    phone: "(03) 5728 0000",
    email: "beechworth.prison@corrections.vic.gov.au"
  },
  "Tarrengower Prison (Maldon)": {
    address: "9 Maldon-Shelbourne Rd, Nuggetty VIC 3463",
    phone: "(03) 5476 1300",
    email: "tarrengower.prison@corrections.vic.gov.au"
  },
  "Judy Lazarus Transition Centre (West Melbourne)": {
    address: "50 Adderley Street, West Melbourne VIC 3003",
    phone: "(03) 9326 6900",
    email: "jltc.prison@corrections.vic.gov.au"
  },
  "Western Plains Correctional Centre (Lara)": {
    address: "1150 Bacchus Marsh Road, Lara VIC 3212",
    phone: "(03) 5220 8222",
    email: "westernplains.prison@corrections.vic.gov.au"
  }
};

// Populate the remaining 334 police stations dynamically
policeStationsData.forEach(station => {
  CUSTODY_LOCATIONS_DETAILS[station.name] = {
    address: station.address,
    phone: station.phone,
    email: station.email
  };
});

const PRISONS = Object.keys(CUSTODY_LOCATIONS_DETAILS).filter(k => !k.endsWith("Police Station"));
const POLICE_STATIONS = Object.keys(CUSTODY_LOCATIONS_DETAILS).filter(k => k.endsWith("Police Station"));

const CENTRELINK_BENEFITS = [
  "Abstudy",
  "Age Pension",
  "Austudy",
  "Carer Payment",
  "Disability Support Pension",
  "JobSeeker Payment",
  "Parenting Payment",
  "Youth Allowance",
  "Partner Allowance",
  "Sickness Allowance",
  "Special Benefit",
  "Veterans War Service Pension",
  "Widow Allowance",
  "Widow B Pension",
  "Wife Pension",
  "Other"
];

const TABS = [
  { id: "client", label: "Client Details" },
  { id: "family", label: "COA & Family Lineage" },
  { id: "health", label: "Health & Financial" },
  { id: "court", label: "Court Details" },
  { id: "charges", label: "Charges & Co-Accused" },
  { id: "checks", label: "Conflicts & Authority" },
  { id: "todo", label: "To Do Checklist" },
];

const DEMO_VALS_DATA = {
  db_entry_date: "2026-05-27",
  entered_by: "CrimSec",
  form_completed_by: "Sarah Jenkins (Paralegal)",
  
  // Key Client Details
  first_name: "Benjamin",
  middle_name: "Robert",
  surname: "Thornton",
  other_known_names: "Benny",
  dob: "1994-08-12",
  gender: "Male",
  in_custody: "Yes",
  custody_type: "Police cells/station",
  custody_location: "Hamilton Police Station",
  crn: "482-991-03A",
  phone: "0491 570 156",
  email: "b.thornton@example.com",
  res_address: "14 Glenelg Highway",
  res_suburb: "Hamilton",
  res_postcode: "3300",
  postal_address: "14 Glenelg Highway",
  postal_suburb: "Hamilton",
  postal_postcode: "3300",
  same_as_postal: true,
  homeless_status: "At Risk",
  literacy_status: "Well",
  auth_person_name: "Margaret Thornton",
  auth_relationship: "Mother",
  auth_phone: "0491 570 882",
  indigenous_identity: "Aboriginal",
  
  // COA
  coa_on_db: "Yes",
  coa_location_db: "Registry-West-2024",
  coa_cert_attached: "Yes",
  coa_documents: [
    { id: "coa-1", name: "COA_Certificate_Benjamin_Thornton.pdf", size: "185 KB", url: "#" }
  ],
  
  // Family Lineage
  mum_name: "Margaret Thornton (née Cooper)",
  dad_name: "Arthur Thornton",
  maternal_grandma: "Evelyn Cooper",
  maternal_grandpa: "George Cooper",
  paternal_grandma: "Alice Thornton",
  paternal_grandpa: "Frank Thornton",
  mob_name: "Gunditjmara",
  accos_involved: "Winda-Mara Aboriginal Co-op",
  other_family_members: "Uncle Roy Cooper, Auntie Clara Cooper",

  // Health Factors
  health_physical: "Type 2 Diabetes, controlled via medication",
  health_intellectual: "N/A",
  health_mental: "Mild ADHD and anxiety, sees counsellor fortnightly",

  // Financial
  income_level: "< $65,000 p/a",
  centrelink_benefit: "JobSeeker Payment",
  financial_crn: "482-991-03A",

  // Court Details
  court_date: "2026-06-15",
  court_type: "Magistrates'",
  court_location: "Hamilton",
  hearing_type: "Mention",
  adjourned_previously: "No",
  adjourned_times: "0",
  client_on_status: "Bail",
  adjournment_required: "Yes",
  vals_date: "Yes",
  consent_to_adjourn: "Yes",
  sent_adjournment: "Yes",
  court_notes: "Requesting adjournment to obtain police brief of evidence and confirm legal aid funding.",
  email_court: "Yes",
  email_to: "hamilton@courts.vic.gov.au",
  email_subject: "Adjournment Request - Benjamin Thornton - VP-8839201 - 15/06/2026",
  email_body: "",

  // Charges
  charges_list: [
    { 
      id: "charge-1", 
      offence_group: "Theft", 
      charge_title: "Motor vehicle theft", 
      informant: "Sgt. D. Miller", 
      vp_number: "VP-8839201", 
      station: "Hamilton Police Station",
      police_brief: { name: "Police_Brief_Hamilton_Theft.pdf", size: "2.1 MB", url: "#" }
    },
    { 
      id: "charge-2", 
      offence_group: "Regulatory driving offences", 
      charge_title: "Licensing offences", 
      informant: "Sgt. D. Miller", 
      vp_number: "VP-8839201", 
      station: "Hamilton Police Station",
      police_brief: null
    }
  ],

  // Co-accused
  co_accused_name: "N/A",
  co_accused_dob: "",
  co_accused_notes: "",

  // Conflicts
  conflict_checked_by: "Sarah Jenkins",
  conflict_check_date: "2026-05-27",
  conflict_status: "No",
  conflict_na_reason: "",
  conflict_details: "",

  // Checks
  client_authority_done: "Yes",
  already_has_lawyer: "No",
  existing_lawyer_name: "",
  applied_for_bail: "N/A",
  consent_to_referral: "Yes",

  // Advice
  advice_instructions: "Advised client regarding the process for applying for legal assistance and bail compliance. Client instructed to seek an adjournment to allow VALS to request police prosecution brief. Scheduled follow-up meeting once briefing is received.",

  // Checklist
  checklist: [
    { id: "todo-1", item: "Perform conflict check on Database", completed: "Yes", completed_by: "SJ" },
    { id: "todo-2", item: "Request Police Brief of Evidence", completed: "No", completed_by: "" },
    { id: "todo-3", item: "Verify Confirmation of Aboriginality (COA)", completed: "Yes", completed_by: "SJ" },
    { id: "todo-4", item: "Draft and submit Adjournment Request to Court", completed: "No", completed_by: "" },
    { id: "todo-5", item: "Confirm legal aid funding application", completed: "No", completed_by: "" }
  ],

  other_notes: "Client was very cooperative. Mother Margaret wishes to remain primary contact point if client is unavailable.",
  sig_name: "Sarah Jenkins",
  sig_date: "2026-05-27",
  custody_documents: [
    { id: "doc-1", name: "Bail_Refusal_Record_Hamilton.pdf", size: "342 KB" }
  ],
};

const EMPTY_FORM = {
  db_entry_date: "",
  entered_by: "",
  form_completed_by: "",
  first_name: "",
  middle_name: "",
  surname: "",
  other_known_names: "",
  dob: "",
  gender: "",
  in_custody: "No",
  custody_type: "",
  custody_location: "",
  crn: "",
  phone: "",
  email: "",
  res_address: "",
  res_suburb: "",
  res_postcode: "",
  postal_address: "",
  postal_suburb: "",
  postal_postcode: "",
  same_as_postal: false,
  homeless_status: "No",
  literacy_status: "Well",
  auth_person_name: "",
  auth_relationship: "",
  auth_phone: "",
  indigenous_identity: "",
  coa_on_db: "No",
  coa_location_db: "",
  coa_cert_attached: "No",
  coa_documents: [],
  mum_name: "",
  dad_name: "",
  maternal_grandma: "",
  maternal_grandpa: "",
  paternal_grandma: "",
  paternal_grandpa: "",
  mob_name: "",
  accos_involved: "",
  other_family_members: "",
  health_physical: "",
  health_intellectual: "",
  health_mental: "",
  income_level: "",
  centrelink_benefit: "",
  financial_crn: "",
  court_date: "",
  court_type: "",
  court_location: "",
  hearing_type: "",
  adjourned_previously: "No",
  adjourned_times: "0",
  client_on_status: "",
  adjournment_required: "No",
  vals_date: "No",
  consent_to_adjourn: "No",
  sent_adjournment: "N/A",
  court_notes: "",
  email_court: "No",
  email_to: "",
  email_subject: "",
  email_body: "",
  charges_list: [],
  co_accused_name: "",
  co_accused_dob: "",
  co_accused_notes: "",
  conflict_checked_by: "",
  conflict_check_date: "",
  conflict_status: "No",
  conflict_na_reason: "",
  conflict_details: "",
  client_authority_done: "No",
  already_has_lawyer: "No",
  existing_lawyer_name: "",
  applied_for_bail: "N/A",
  consent_to_referral: "N/A",
  advice_instructions: "",
  checklist: [
    { id: "todo-1", item: "Perform conflict check on Database", completed: "No", completed_by: "" },
    { id: "todo-2", item: "Request Police Brief of Evidence", completed: "No", completed_by: "" },
    { id: "todo-3", item: "Verify Confirmation of Aboriginality (COA)", completed: "No", completed_by: "" },
    { id: "todo-4", item: "Draft and submit Adjournment Request to Court", completed: "No", completed_by: "" },
    { id: "todo-5", item: "Confirm legal aid funding application", completed: "No", completed_by: "" }
  ],
  other_notes: "",
  sig_name: "",
  sig_date: "",
  custody_documents: [],
};

// Charges under the chosen heading, plus an older saved charge that's no longer in the list.
const chargeOptions = (charge) => {
  const options = OFFENCE_CLASSIFICATION[withoutOffenceCode(charge.offence_group)] || [];
  return charge.charge_title && !options.includes(charge.charge_title) ? [...options, charge.charge_title] : options;
};

const TABS_ORDER = ["client", "family", "health", "court", "charges", "checks", "todo"];

export default function VALSIntakeForm({ isPortalMode, brandType = "vals" }) {
  const logIntakeSubmissionToOutbox = async (customSubject = null, customBody = null) => {
    try {
      const clientName = `${formData.first_name || ""} ${formData.surname || ""}`.trim() || "Unnamed Client";
      const brandName = orgShort || "VALS";
      
      const emailSubject = customSubject || `${brandName} Criminal Intake - ${clientName}`;
      
      let bodyText = "";
      if (customBody) {
        bodyText = customBody;
      } else {
        let chargesSummary = "";
        if (Array.isArray(formData.charges_list) && formData.charges_list.length > 0) {
          chargesSummary = formData.charges_list.map((c, idx) => `  ${idx + 1}. Description: ${c.charge_title || "N/A"} | Group: ${withoutOffenceCode(c.offence_group) || "N/A"} | Informant: ${c.informant || "N/A"}`).join("\n");
        } else {
          chargesSummary = "  - No charges logged";
        }

        bodyText = `
Criminal Intake Form Summary
---------------------------
Client Name: ${clientName}
DOB: ${formData.dob || "N/A"}
Custody Status: ${formData.in_custody === "Yes" ? `In Custody (${formData.custody_location || "N/A"})` : "Not in Custody"}
CRN: ${formData.crn || "N/A"}
Phone: ${formData.phone || "N/A"}
Email: ${formData.email || "N/A"}
Address: ${formData.res_address || ""} ${formData.res_suburb || ""} ${formData.res_postcode || ""}
Same as Postal: ${formData.same_as_postal ? "Yes" : "No"}
Homeless Status: ${formData.homeless_status || "N/A"}
Indigenous Identity: ${formData.indigenous_identity || "N/A"}
Mob/Clan Name: ${formData.mob_name || "N/A"}

Court Information:
  Court Date: ${formData.court_date || "N/A"}
  Jurisdiction: ${formData.court_type || "N/A"}
  Location: ${formData.court_location || "N/A"}
  Hearing Type: ${formData.hearing_type || "N/A"}
  Adjournment Required: ${formData.adjournment_required || "N/A"}
  Consent to Adjourn: ${formData.consent_to_adjourn || "N/A"}

Charges:
${chargesSummary}

Conflict Check:
  Checked By: ${formData.conflict_checked_by || "N/A"}
  Date: ${formData.conflict_check_date || "N/A"}
  Conflict Found: ${formData.conflict_status || "N/A"}

Advice Instructions:
${formData.advice_instructions || "No advice recorded"}
        `.trim();
      }

      await base44.integrations.Core.SendEmail({
        to: INTAKE_EMAIL,
        cc: "",
        subject: emailSubject,
        body: bodyText
      });
    } catch (err) {
      console.warn("Failed to write criminal intake to outbox:", err);
    }
  };

  const brandKey = brandType ? `${brandType}_` : "";
  const vlaCompletedKey = `${brandKey}vla_completed_list`;
  const vlaDraftsKey = `${brandKey}vla_drafts_list`;
  const demographicsKey = `${brandKey}active_client_demographics`;
  const vlaPrefillKey = `${brandKey}vla_prefill_pending`;
  const ataListKey = `${brandKey}vals_completed_atas_list`;
  const singleAtaKey = `${brandKey}vals_completed_ata`;
  const ataPrefillKey = `${brandKey}vals_ata_prefill_pending`;

  const orgName = brandType === "lacw" ? "Law and Advocacy Centre for Women" : brandType === "jbl" ? "Jarrod Behan Law" : "Victorian Aboriginal Legal Service";
  const orgShort = brandType === "lacw" ? "LACW" : brandType === "jbl" ? "JBL" : "VALS";
  const orgAddress = brandType === "lacw" ? "RMIT Building 152, Level 1 · 147-155 Pelham Street, Carlton VIC 3053" : brandType === "jbl" ? "Melbourne VIC 3000" : "273 High St, Preston VIC 3072";

  const [formData, setFormData] = useState(EMPTY_FORM);
  const [activeTab, setActiveTab] = useState("client");
  const [showExportDialog, setShowExportDialog] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isPrinting, setIsPrinting] = useState(false);
  const [showPrefillModal, setShowPrefillModal] = useState(false);
  const [selectedPrefillForms, setSelectedPrefillForms] = useState({ ata: true, vla: true });

  // Integration States
  const [completedVlas, setCompletedVlas] = useState([]);
  const [completedAtas, setCompletedAtas] = useState([]);
  // Actionstep integration handled by ActionstepSavePanel + useActionstepSession hook

  const scanCompletedForms = useCallback(() => {
    try {
      const vlaListStr = clientStorage.getItem(vlaCompletedKey);
      let vlaList = [];
      if (vlaListStr) {
        try { vlaList = JSON.parse(vlaListStr); } catch (e) {}
      }
      
      const vlaDraftsStr = clientStorage.getItem(vlaDraftsKey);
      if (vlaDraftsStr) {
        try {
          const drafts = JSON.parse(vlaDraftsStr);
          drafts.forEach(d => {
            if (!vlaList.some(v => v.id === d.id)) {
              vlaList.push({
                id: d.id,
                applicant_name: d.applicant_name,
                completed_at: d.updated_at,
                current_step: d.current_step,
                type: 'VLA_DRAFT'
              });
            }
          });
        } catch (e) {}
      }
      setCompletedVlas(vlaList);

      const ataListStr = clientStorage.getItem(ataListKey);
      if (ataListStr) {
        try { setCompletedAtas(JSON.parse(ataListStr)); } catch (e) {}
      } else {
        const singleAta = clientStorage.getItem(singleAtaKey);
        if (singleAta) {
          try { setCompletedAtas([JSON.parse(singleAta)]); } catch (e) {}
        } else {
          setCompletedAtas([]);
        }
      }
    } catch (e) {
      console.error("Failed to scan completed forms:", e);
    }
  }, []);

  useEffect(() => {
    scanCompletedForms();
    window.addEventListener("storage", scanCompletedForms);
    window.addEventListener("focus", scanCompletedForms);
    return () => {
      window.removeEventListener("storage", scanCompletedForms);
      window.removeEventListener("focus", scanCompletedForms);
    };
  }, [scanCompletedForms]);

  // Autosave client demographics to clientStorage for cross-form pre-filling
  useEffect(() => {
    if (formData) {
      const demographics = {
        first_name: formData.first_name || "",
        surname: formData.surname || "",
        dob: formData.dob || "",
        gender: formData.gender || "",
        email: formData.email || "",
        phone: formData.phone || "",
        res_address: formData.res_address || "",
        res_suburb: formData.res_suburb || "",
        res_postcode: formData.res_postcode || "",
        financial_crn: formData.financial_crn || formData.crn || "",
        indigenous_identity: formData.indigenous_identity || "",
        centrelink_benefit: formData.centrelink_benefit || "",
        in_custody: formData.in_custody || ""
      };
      clientStorage.setItem(demographicsKey, JSON.stringify(demographics));
    }
  }, [demographicsKey,
    formData.first_name,
    formData.surname,
    formData.dob,
    formData.gender,
    formData.email,
    formData.phone,
    formData.res_address,
    formData.res_suburb,
    formData.res_postcode,
    formData.financial_crn,
    formData.crn,
    formData.indigenous_identity,
    formData.centrelink_benefit,
    formData.in_custody
  ]);

  const handleFieldChange = useCallback((field, value) => {
    setFormData(prev => {
      const updated = { ...prev, [field]: value };
      
      // If adjournment_required is changed to No, also turn off email_court
      if (field === "adjournment_required" && value === "No") {
        updated.email_court = "No";
      }

      // If email_court is changed to Yes, and email_body is empty, pre-fill it!
      if (field === "email_court" && value === "Yes") {
        updated.email_to = updated.email_to || getCourtCoordinatorEmail(prev.court_location, prev.court_type);
        updated.email_subject = updated.email_subject || generateDefaultEmailSubject(updated);
        updated.email_body = updated.email_body || generateDefaultEmailBody(updated);
      }

      if (prev.same_as_postal) {
        if (field === "postal_address") updated.res_address = value;
        if (field === "postal_suburb") updated.res_suburb = value;
        if (field === "postal_postcode") updated.res_postcode = value;
      }
      return updated;
    });
  }, []);

  const handleResAddressChange = useCallback((name, value) => {
    if (value.includes(",")) {
      const parsed = parseAddressString(value);
      setFormData(prev => ({
        ...prev,
        res_address: parsed.street,
        res_suburb: parsed.suburb || prev.res_suburb,
        res_postcode: parsed.postcode || prev.res_postcode
      }));
    } else {
      setFormData(prev => ({ ...prev, res_address: value }));
    }
  }, []);

  const handleResSameAsPostalChange = useCallback((checked) => {
    setFormData(prev => {
      const updated = { ...prev, same_as_postal: checked };
      if (checked) {
        updated.res_address = prev.postal_address;
        updated.res_suburb = prev.postal_suburb;
        updated.res_postcode = prev.postal_postcode;
      }
      return updated;
    });
  }, []);

  const handleRegenerateEmail = () => {
    setFormData(prev => ({
      ...prev,
      email_to: getCourtCoordinatorEmail(prev.court_location, prev.court_type),
      email_subject: generateDefaultEmailSubject(prev),
      email_body: generateDefaultEmailBody(prev)
    }));
    toast.success("Email draft regenerated from current form values.");
  };

  const handleNextTab = () => {
    const currentIndex = TABS_ORDER.indexOf(activeTab);
    if (currentIndex < TABS_ORDER.length - 1) {
      setActiveTab(TABS_ORDER[currentIndex + 1]);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handlePrevTab = () => {
    const currentIndex = TABS_ORDER.indexOf(activeTab);
    if (currentIndex > 0) {
      setActiveTab(TABS_ORDER[currentIndex - 1]);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handlePrefillOtherForms = () => {
    // Force save active demographics to clientStorage
    const demographics = {
      first_name: formData.first_name || "",
      surname: formData.surname || "",
      dob: formData.dob || "",
      gender: formData.gender || "",
      email: formData.email || "",
      phone: formData.phone || "",
      res_address: formData.res_address || "",
      res_suburb: formData.res_suburb || "",
      res_postcode: formData.res_postcode || "",
      financial_crn: formData.financial_crn || formData.crn || "",
      indigenous_identity: formData.indigenous_identity || "",
      centrelink_benefit: formData.centrelink_benefit || "",
      in_custody: formData.in_custody || ""
    };
    clientStorage.setItem(demographicsKey, JSON.stringify(demographics));

    let openedAny = false;
    if (selectedPrefillForms.ata) {
      clientStorage.setItem(ataPrefillKey, 'true');
      window.open(createPageUrl("VALSAuthorityToAct"), "_blank");
      openedAny = true;
    }
    if (selectedPrefillForms.vla) {
      clientStorage.setItem(vlaPrefillKey, 'true');
      window.open(createPageUrl("LegalAidForm"), "_blank");
      openedAny = true;
    }

    if (openedAny) {
      toast.success("Client details stored. Selected forms pre-filled and opened in new tabs!");
      setShowPrefillModal(false);
    } else {
      toast.warning("Please select at least one form to prefill.");
    }
  };

  const handlePostalAddressChange = useCallback((name, value) => {
    if (value.includes(",")) {
      const parsed = parseAddressString(value);
      setFormData(prev => {
        const updated = {
          ...prev,
          postal_address: parsed.street,
          postal_suburb: parsed.suburb || prev.postal_suburb,
          postal_postcode: parsed.postcode || prev.postal_postcode
        };
        if (prev.same_as_postal) {
          updated.res_address = updated.postal_address;
          updated.res_suburb = updated.postal_suburb;
          updated.res_postcode = updated.postal_postcode;
        }
        return updated;
      });
    } else {
      setFormData(prev => {
        const updated = { ...prev, postal_address: value };
        if (prev.same_as_postal) {
          updated.res_address = value;
        }
        return updated;
      });
    }
  }, []);

  const handleCoaUpload = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    let formattedSize = `${(file.size / 1024).toFixed(0)} KB`;
    if (file.size > 1024 * 1024) {
      formattedSize = `${(file.size / (1024 * 1024)).toFixed(1)} MB`;
    }
    
    const fileUrl = URL.createObjectURL(file);
    const newDoc = {
      id: `coa-${Date.now()}`,
      name: file.name,
      size: formattedSize,
      url: fileUrl
    };
    
    setFormData(prev => ({
      ...prev,
      coa_documents: [...(prev.coa_documents || []), newDoc]
    }));
    toast.success(`Attached COA Certificate: ${file.name}`);
  };

  const removeCoaDocument = (docId) => {
    setFormData(prev => ({
      ...prev,
      coa_documents: (prev.coa_documents || []).filter(d => d.id !== docId)
    }));
    toast.info("Removed COA Certificate.");
  };

  const handleChargeChange = (index, field, value) => {
    setFormData(prev => {
      const nextCharges = [...prev.charges_list];
      nextCharges[index] = { ...nextCharges[index], [field]: value };
      return { ...prev, charges_list: nextCharges };
    });
  };

  const addCharge = () => {
    setFormData(prev => ({
      ...prev,
      charges_list: [
        ...prev.charges_list,
        {
          id: `charge-${Date.now()}`,
          offence_group: "",
          charge_title: "",
          informant: "",
          vp_number: "",
          station: "",
          police_brief: null
        }
      ]
    }));
    toast.success("Added new charge record.");
  };

  const handleBriefUpload = (index, event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    let formattedSize = `${(file.size / 1024).toFixed(0)} KB`;
    if (file.size > 1024 * 1024) {
      formattedSize = `${(file.size / (1024 * 1024)).toFixed(1)} MB`;
    }
    
    const fileUrl = URL.createObjectURL(file);
    
    setFormData(prev => {
      const nextCharges = [...prev.charges_list];
      nextCharges[index] = {
        ...nextCharges[index],
        police_brief: {
          name: file.name,
          size: formattedSize,
          url: fileUrl
        }
      };
      return { ...prev, charges_list: nextCharges };
    });
    toast.success(`Attached police brief: ${file.name}`);
  };

  const removeBrief = (index) => {
    setFormData(prev => {
      const nextCharges = [...prev.charges_list];
      nextCharges[index] = {
        ...nextCharges[index],
        police_brief: null
      };
      return { ...prev, charges_list: nextCharges };
    });
    toast.info("Removed police brief attachment.");
  };

  const handleCustodyDocumentUpload = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    let formattedSize = `${(file.size / 1024).toFixed(0)} KB`;
    if (file.size > 1024 * 1024) {
      formattedSize = `${(file.size / (1024 * 1024)).toFixed(1)} MB`;
    }
    
    const fileUrl = URL.createObjectURL(file);
    const newDoc = {
      id: `doc-${Date.now()}`,
      name: file.name,
      size: formattedSize,
      url: fileUrl
    };
    
    setFormData(prev => ({
      ...prev,
      custody_documents: [...prev.custody_documents, newDoc]
    }));
    toast.success(`Uploaded custody document: ${file.name}`);
  };

  const removeCustodyDocument = (docId) => {
    setFormData(prev => ({
      ...prev,
      custody_documents: prev.custody_documents.filter(d => d.id !== docId)
    }));
    toast.info("Removed custody document.");
  };

  const removeCharge = (index) => {
    setFormData(prev => ({
      ...prev,
      charges_list: prev.charges_list.filter((_, i) => i !== index)
    }));
    toast.info("Removed charge record.");
  };

  const handleTodoChange = (index, field, value) => {
    setFormData(prev => {
      const nextChecklist = [...prev.checklist];
      nextChecklist[index] = { ...nextChecklist[index], [field]: value };
      return { ...prev, checklist: nextChecklist };
    });
  };

  const loadDemoCase = () => {
    setFormData(DEMO_VALS_DATA);
    toast.success(`Loaded demo ${orgShort} intake case.`);
  };

  const resetForm = () => {
    setFormData(EMPTY_FORM);
    toast.info("Form reset.");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.first_name || !formData.surname) {
      toast.error("Client Name is required.");
      return;
    }
    
    setIsSaving(true);
    await logIntakeSubmissionToOutbox();
    await new Promise(resolve => setTimeout(resolve, 1000));
    setIsSaving(false);
    toast.success(`${orgShort} Criminal Intake successfully saved to client database.`);
  };

  // Build intake form note text for Actionstep
  const buildIntakeNoteText = () => [
    `CLIENT: ${formData.first_name || ""} ${formData.surname || ""}`.trim(),
    `DOB: ${formData.dob || "N/A"}`,
    `ADDRESS: ${formData.res_address || "N/A"}`,
    `PHONE: ${formData.mobile || formData.home_phone || "N/A"}`,
    `EMAIL: ${formData.email || "N/A"}`,
    ``,
    `MATTER:`,
    `  Court: ${formData.court_type || "N/A"} ${formData.court_location ? `at ${formData.court_location}` : ""}`,
    `  Next Appearance: ${formData.next_court_date || "N/A"}`,
    `  Charges: ${formData.charges ? formData.charges.map(c => c.offence || c).join("; ") : "N/A"}`,
    `  In Custody: ${formData.in_custody ? "Yes" : "No"}`,
    ``,
    `LAWYER: ${formData.lawyer_assigned || "N/A"}`,
    `FILE REF: ${formData.file_ref || "N/A"}`,
    `INTAKE DATE: ${formData.intake_date || new Date().toLocaleDateString("en-AU")}`,
  ].join("\n");

  const handlePrint = async () => {
    setShowExportDialog(false);
    await logIntakeSubmissionToOutbox(null, `[Printed/Exported PDF Intake File]\n\n` + (formData.first_name ? "" : "Blank Form"));
    setTimeout(() => window.print(), 300);
  };

  return (
    <div className="min-h-screen bg-slate-50 font-sans pb-12 print:bg-white print:pb-0">
      <div className="print:hidden">
      
      {/* Top Navigation */}
      <div className="sticky top-0 z-20 bg-white border-b border-slate-200 shadow-sm print:hidden">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            {!isPortalMode && (
              <>
                <a
                  href={createPageUrl("Home")}
                  className="text-slate-500 hover:text-purple-600 transition-colors flex items-center gap-1.5 text-sm font-semibold uppercase tracking-wider"
                >
                  <ArrowLeft className="w-4 h-4" />
                  Back
                </a>
                <div className="h-6 w-px bg-slate-200 mx-2" />
              </>
            )}
            <div>
              <h1 className="text-lg font-bold text-slate-900 flex items-center gap-2">
                {!isPortalMode && (
                  brandType === "lacw" ? (
                    <LacwWordmark compact />
                  ) : brandType === "jbl" ? (
                    <img
                      src="/jbl-logo.png"
                      alt="JBL Logo"
                      className="h-7 w-auto object-contain"
                    />
                  ) : (
                    <img
                      src="/vals-logo.png"
                      alt="VALS Logo"
                      className="h-7 w-auto object-contain"
                    />
                  )
                )}
                {orgShort} Criminal Intake Form
              </h1>
              <p className="text-xs text-slate-500">{orgName} · Staff intake panel</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => setShowPrefillModal(true)} className="text-xs font-semibold text-red-600 border-red-200 hover:bg-red-50">
              <RefreshCw className="w-3.5 h-3.5 mr-1" /> Pre-fill Other Forms
            </Button>
            {isDemoMode && (
              <Button variant="outline" size="sm" onClick={loadDemoCase} className="text-xs font-semibold text-red-600 border-red-200 hover:bg-red-50">
                Load Demo Document Data
              </Button>
            )}
            <Button variant="outline" size="sm" onClick={resetForm} className="text-xs font-semibold text-slate-500">
              <RotateCcw className="w-3.5 h-3.5 mr-1" /> Reset
            </Button>
            <Button size="sm" onClick={() => setShowExportDialog(true)} className="bg-red-600 hover:bg-red-700 text-white font-semibold text-xs gap-1.5">
              <Printer className="w-3.5 h-3.5" /> Export / Print
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 mt-6">
        
        {/* Navigation Tabs */}
        <div className="flex border-b border-slate-200 bg-white rounded-t-xl overflow-x-auto print:hidden shadow-sm">
          {[
            { id: "client", label: "Client Details", icon: User },
            { id: "family", label: "COA & Family Lineage", icon: Award },
            { id: "health", label: "Health & Financial", icon: Heart },
            { id: "court", label: "Court Details", icon: Calendar },
            { id: "charges", label: "Charges & Co-Accused", icon: FileText },
            { id: "checks", label: "Conflicts & Authority", icon: ShieldCheck },
            { id: "todo", label: "To Do Checklist", icon: ListTodo },
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-6 py-4 text-xs font-bold uppercase tracking-wider border-b-2 transition-all whitespace-nowrap ${
                activeTab === tab.id 
                  ? "border-red-600 text-red-600 bg-red-50/10" 
                  : "border-transparent text-slate-500 hover:text-red-500 hover:bg-slate-50/50"
              }`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Form Elements content area */}
        <div id="vals-intake-content" className="bg-white rounded-b-xl border border-t-0 border-slate-200 p-6 sm:p-8 shadow-sm space-y-8">
          

          <form onSubmit={handleSubmit} className="space-y-6">
            
            {/* TAB: Client Details */}
            {(activeTab === "client" || typeof window === 'undefined') && (
              <div className="space-y-6">
                <h3 className="text-sm font-bold text-slate-950 uppercase tracking-wider flex items-center gap-2 border-b border-slate-100 pb-2">
                  <User className="w-4.5 h-4.5 text-red-600" />
                  Key Client Details
                </h3>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="space-y-1.5">
                    <Label className="text-xs font-semibold text-slate-700">First Name *</Label>
                    <Input 
                      value={formData.first_name} 
                      onChange={e => handleFieldChange("first_name", e.target.value)} 
                      required
                      placeholder="Given Name"
                      className="text-sm rounded-lg"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs font-semibold text-slate-700">Middle Name</Label>
                    <Input 
                      value={formData.middle_name} 
                      onChange={e => handleFieldChange("middle_name", e.target.value)} 
                      placeholder="Middle Name"
                      className="text-sm rounded-lg"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs font-semibold text-slate-700">Surname *</Label>
                    <Input 
                      value={formData.surname} 
                      onChange={e => handleFieldChange("surname", e.target.value)} 
                      required
                      placeholder="Family Name"
                      className="text-sm rounded-lg"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div className="space-y-1.5">
                    <Label className="text-xs font-semibold text-slate-700">Other Known Name(s) / Alias</Label>
                    <Input 
                      value={formData.other_known_names} 
                      onChange={e => handleFieldChange("other_known_names", e.target.value)} 
                      placeholder="e.g. Benny"
                      className="text-sm rounded-lg"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs font-semibold text-slate-700">Date of Birth</Label>
                    <Input 
                      type="date" 
                      value={formData.dob} 
                      onChange={e => handleFieldChange("dob", e.target.value)} 
                      className="text-sm rounded-lg"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs font-semibold text-slate-700">Gender</Label>
                    <Select value={formData.gender} onValueChange={v => handleFieldChange("gender", v)}>
                      <SelectTrigger className="text-sm rounded-lg">
                        <SelectValue placeholder="Select" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Male">Male</SelectItem>
                        <SelectItem value="Female">Female</SelectItem>
                        <SelectItem value="Other">Other / Self-described</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs font-semibold text-slate-700">Client is:</Label>
                    <Select value={formData.indigenous_identity} onValueChange={v => handleFieldChange("indigenous_identity", v)}>
                      <SelectTrigger className="text-sm rounded-lg">
                        <SelectValue placeholder="Indigenous Status" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Aboriginal">Aboriginal</SelectItem>
                        <SelectItem value="Torres Strait Islander">Torres Strait Islander</SelectItem>
                        <SelectItem value="Both">Both Aboriginal &amp; Torres Strait Islander</SelectItem>
                        <SelectItem value="No">Neither</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 border-t border-slate-100 pt-4">
                  <div className="space-y-1.5">
                    <Label className="text-xs font-semibold text-slate-700">In Custody?</Label>
                    <Select value={formData.in_custody} onValueChange={v => {
                      handleFieldChange("in_custody", v);
                      if (v === "No") {
                        handleFieldChange("custody_type", "");
                        handleFieldChange("custody_location", "");
                      } else {
                        handleFieldChange("custody_type", "Police cells/station");
                        handleFieldChange("custody_location", "Hamilton Police Station");
                      }
                    }}>
                      <SelectTrigger className="text-sm rounded-lg">
                        <SelectValue placeholder="Select" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Yes">Yes</SelectItem>
                        <SelectItem value="No">No</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs font-semibold text-slate-700">Custody Location Type</Label>
                    <Select 
                      value={formData.custody_type} 
                      onValueChange={v => {
                        handleFieldChange("custody_type", v);
                        handleFieldChange("custody_location", v === "Prison" ? "Barwon Prison (Lara)" : "Hamilton Police Station");
                      }}
                      disabled={formData.in_custody === "No"}
                    >
                      <SelectTrigger className="text-sm rounded-lg">
                        <SelectValue placeholder="Select Type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Prison">Prison</SelectItem>
                        <SelectItem value="Police cells/station">Police cells/station</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs font-semibold text-slate-700">Custody Location</Label>
                    <Select 
                      value={formData.custody_location} 
                      onValueChange={v => handleFieldChange("custody_location", v)}
                      disabled={formData.in_custody === "No" || !formData.custody_type}
                    >
                      <SelectTrigger className="text-sm rounded-lg">
                        <SelectValue placeholder="Select Facility" />
                      </SelectTrigger>
                      <SelectContent>
                        {formData.custody_type === "Prison" ? (
                          PRISONS.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)
                        ) : (
                          POLICE_STATIONS.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)
                        )}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs font-semibold text-slate-700">CRN (Client Reference Number)</Label>
                    <Input 
                      value={formData.crn} 
                      onChange={e => handleFieldChange("crn", e.target.value)} 
                      placeholder="e.g. 482-991-03A"
                      className="text-sm font-mono rounded-lg"
                    />
                  </div>

                  {/* Dynamic Custody Location Contact Details & Paperwork */}
                  {formData.in_custody === "Yes" && formData.custody_location && CUSTODY_LOCATIONS_DETAILS[formData.custody_location] && (
                    <div className="col-span-full space-y-4 mt-2 text-left">
                      {/* Custody Info Card */}
                      <div className="bg-red-50/20 border border-red-100/50 rounded-xl p-4 flex flex-col md:flex-row md:items-center justify-between gap-4">
                        <div className="space-y-1">
                          <span className="text-[10px] font-bold uppercase tracking-wider text-red-600 block">Custody Facility Details</span>
                          <h4 className="text-sm font-bold text-slate-900">{formData.custody_location}</h4>
                          <div className="text-xs text-slate-600 flex items-center gap-1.5">
                            <Building2 className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                            <span>{CUSTODY_LOCATIONS_DETAILS[formData.custody_location].address}</span>
                          </div>
                        </div>
                        <div className="flex flex-col sm:flex-row gap-4 text-xs shrink-0 border-t md:border-t-0 pt-3 md:pt-0 border-slate-100">
                          <div className="flex items-center gap-2 text-slate-600">
                            <Phone className="w-4 h-4 text-red-500" />
                            <div>
                              <span className="text-[10px] text-slate-400 block leading-none">Phone</span>
                              <span className="font-semibold">{CUSTODY_LOCATIONS_DETAILS[formData.custody_location].phone}</span>
                            </div>
                          </div>
                          <div className="flex items-center gap-2 text-slate-600">
                            <Mail className="w-4 h-4 text-red-500" />
                            <div>
                              <span className="text-[10px] text-slate-400 block leading-none">Email</span>
                              <a href={`mailto:${CUSTODY_LOCATIONS_DETAILS[formData.custody_location].email}`} className="font-semibold text-red-600 hover:underline">
                                {CUSTODY_LOCATIONS_DETAILS[formData.custody_location].email}
                              </a>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Custody Paperwork Upload Section */}
                      <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 space-y-3">
                        <div className="flex items-center justify-between">
                          <h5 className="text-xs font-bold text-slate-700 uppercase tracking-wide flex items-center gap-1.5">
                            <Paperclip className="w-4 h-4 text-red-600" /> Attached Custody Paperwork (e.g. Remand Warrant)
                          </h5>
                          <div>
                            <input 
                              type="file" 
                              id="custody-paperwork-upload" 
                              className="hidden" 
                              onChange={handleCustodyDocumentUpload}
                            />
                            <label 
                              htmlFor="custody-paperwork-upload"
                              className="cursor-pointer text-xs font-bold text-red-700 hover:text-red-900 bg-red-50 hover:bg-red-100 border border-red-200 px-3 py-1.5 rounded-lg flex items-center gap-1.5 transition-colors"
                            >
                              <Upload className="w-3.5 h-3.5" /> Attach Document
                            </label>
                          </div>
                        </div>

                        {formData.custody_documents.length === 0 ? (
                          <p className="text-xs text-slate-400 italic">No custody paperwork attached.</p>
                        ) : (
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-2">
                            {formData.custody_documents.map(doc => (
                              <div key={doc.id} className="p-2.5 bg-white border border-slate-200 rounded-lg flex items-center justify-between gap-2 overflow-hidden shadow-sm">
                                <div className="flex items-center gap-2 overflow-hidden">
                                  <span className="w-1.5 h-1.5 bg-red-500 rounded-full shrink-0" />
                                  <span className="text-xs font-semibold text-slate-800 truncate" title={doc.name}>
                                    {doc.name}
                                  </span>
                                  <span className="text-[10px] text-slate-400 shrink-0">
                                    ({doc.size})
                                  </span>
                                </div>
                                <div className="flex items-center gap-1.5 shrink-0">
                                  <a 
                                    href={doc.url} 
                                    target="_blank" 
                                    rel="noopener noreferrer"
                                    className="text-xs font-bold text-slate-700 hover:text-slate-900 bg-slate-50 hover:bg-slate-100 border border-slate-200 px-2 py-1 rounded shadow-sm"
                                  >
                                    View
                                  </a>
                                  <Button 
                                    type="button" 
                                    variant="ghost" 
                                    size="sm" 
                                    className="h-7 w-7 p-0 text-red-650 hover:text-red-800 hover:bg-red-50"
                                    onClick={() => removeCustodyDocument(doc.id)}
                                  >
                                    <Trash2 className="w-3.5 h-3.5" />
                                  </Button>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 border-t border-slate-100 pt-4">
                  <div className="space-y-1.5">
                    <Label className="text-xs font-semibold text-slate-700">Phone Number(s)</Label>
                    <Input 
                      value={formData.phone} 
                      onChange={e => handleFieldChange("phone", e.target.value)} 
                      placeholder="Primary contact numbers"
                      className="text-sm rounded-lg"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs font-semibold text-slate-700">Email Address</Label>
                    <Input 
                      type="email" 
                      value={formData.email} 
                      onChange={e => handleFieldChange("email", e.target.value)} 
                      placeholder="client@email.com"
                      className="text-sm rounded-lg"
                    />
                  </div>
                </div>

                {/* Residential & Postal Addresses */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 border-t border-slate-100 pt-4">
                  {(!formData.same_as_postal || !isPrinting) && (
                    <div className={`space-y-2 ${formData.same_as_postal ? 'print:hidden' : ''}`}>
                      <div className="flex items-center justify-between">
                        <Label className="text-sm font-semibold text-slate-700">Residential Address</Label>
                        <label className="flex items-center gap-1.5 text-xs text-slate-500 cursor-pointer select-none print:hidden">
                          <input
                            type="checkbox"
                            checked={formData.same_as_postal || false}
                            onChange={e => handleResSameAsPostalChange(e.target.checked)}
                            className="h-3.5 w-3.5 rounded border-slate-300 text-red-600 focus:ring-red-500 cursor-pointer"
                          />
                          <span>Same as postal address</span>
                        </label>
                      </div>
                      <AddressAutocomplete 
                        label=""
                        name="res_address" 
                        value={formData.res_address} 
                        onChange={handleResAddressChange} 
                        placeholder="Street Address"
                        disabled={formData.same_as_postal}
                      />
                      <div className="grid grid-cols-2 gap-2 pt-1">
                        <Input 
                          value={formData.res_suburb} 
                          onChange={e => handleFieldChange("res_suburb", e.target.value)} 
                          placeholder="Suburb"
                          disabled={formData.same_as_postal}
                          className="text-sm rounded-lg"
                        />
                        <Input 
                          value={formData.res_postcode} 
                          onChange={e => handleFieldChange("res_postcode", e.target.value)} 
                          placeholder="Postcode (e.g. 3300)"
                          disabled={formData.same_as_postal}
                          className="text-sm rounded-lg font-mono"
                        />
                      </div>
                    </div>
                  )}

                  <div className={`space-y-2 ${(formData.same_as_postal && isPrinting) ? 'col-span-2' : ''} ${formData.same_as_postal ? 'print:col-span-2' : ''}`}>
                    <AddressAutocomplete 
                      label="Postal Address" 
                      name="postal_address" 
                      value={formData.postal_address} 
                      onChange={handlePostalAddressChange} 
                      placeholder="Postal / PO Box Address"
                    />
                    <div className="grid grid-cols-2 gap-2 pt-1">
                      <Input 
                        value={formData.postal_suburb} 
                        onChange={e => handleFieldChange("postal_suburb", e.target.value)} 
                        placeholder="Suburb"
                        className="text-sm rounded-lg"
                      />
                      <Input 
                        value={formData.postal_postcode} 
                        onChange={e => handleFieldChange("postal_postcode", e.target.value)} 
                        placeholder="Postcode"
                        className="text-sm rounded-lg font-mono"
                      />
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 border-t border-slate-100 pt-4">
                  <div className="space-y-1.5">
                    <Label className="text-xs font-semibold text-slate-700">Homelessness Status</Label>
                    <Select value={formData.homeless_status} onValueChange={v => handleFieldChange("homeless_status", v)}>
                      <SelectTrigger className="text-sm rounded-lg">
                        <SelectValue placeholder="Select" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Yes">Yes (Homeless)</SelectItem>
                        <SelectItem value="No">No (Housed)</SelectItem>
                        <SelectItem value="At Risk">At Risk of Homelessness</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs font-semibold text-slate-700">Literacy Level</Label>
                    <Select value={formData.literacy_status} onValueChange={v => handleFieldChange("literacy_status", v)}>
                      <SelectTrigger className="text-sm rounded-lg">
                        <SelectValue placeholder="Select" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Well">Well (Good literacy)</SelectItem>
                        <SelectItem value="Not Very Well">Not Very Well</SelectItem>
                        <SelectItem value="Not At All">Not At All</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="border-t border-slate-100 pt-4">
                  <h4 className="text-xs font-bold text-slate-700 uppercase tracking-wide mb-3">Authorized Contact Person</h4>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="space-y-1.5">
                      <Label className="text-xs font-semibold text-slate-700">Contact Name</Label>
                      <Input 
                        value={formData.auth_person_name} 
                        onChange={e => handleFieldChange("auth_person_name", e.target.value)} 
                        placeholder="e.g. Margaret Thornton"
                        className="text-sm rounded-lg"
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs font-semibold text-slate-700">Relationship to Client</Label>
                      <Input 
                        value={formData.auth_relationship} 
                        onChange={e => handleFieldChange("auth_relationship", e.target.value)} 
                        placeholder="e.g. Mother"
                        className="text-sm rounded-lg"
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs font-semibold text-slate-700">Contact Phone Number</Label>
                      <Input 
                        value={formData.auth_phone} 
                        onChange={e => handleFieldChange("auth_phone", e.target.value)} 
                        placeholder="Phone Number"
                        className="text-sm rounded-lg"
                      />
                    </div>
                  </div>
                </div>

              </div>
            )}

            {/* TAB: Confirmation of Aboriginality & Family Tree */}
            {activeTab === "family" && (
              <div className="space-y-6">
                <h3 className="text-sm font-bold text-slate-950 uppercase tracking-wider flex items-center gap-2 border-b border-slate-100 pb-2">
                  <Award className="w-4.5 h-4.5 text-red-600" />
                  Confirmation of Aboriginality (COA) &amp; Mob Connection
                </h3>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="space-y-1.5">
                    <Label className="text-xs font-semibold text-slate-700">COA on Database?</Label>
                    <Select value={formData.coa_on_db} onValueChange={v => handleFieldChange("coa_on_db", v)}>
                      <SelectTrigger className="text-sm rounded-lg">
                        <SelectValue placeholder="Select" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Yes">Yes</SelectItem>
                        <SelectItem value="No">No</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs font-semibold text-slate-700">Location on Database</Label>
                    <Input 
                      value={formData.coa_location_db} 
                      onChange={e => handleFieldChange("coa_location_db", e.target.value)} 
                      placeholder="e.g. Registry folder"
                      className="text-sm rounded-lg"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs font-semibold text-slate-700">COA Certificate Attached?</Label>
                    <Select 
                      value={formData.coa_cert_attached} 
                      onValueChange={v => {
                        handleFieldChange("coa_cert_attached", v);
                        if (v === "No") {
                          setFormData(prev => ({ ...prev, coa_documents: [] }));
                        }
                      }}
                    >
                      <SelectTrigger className="text-sm rounded-lg">
                        <SelectValue placeholder="Select" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Yes">Yes</SelectItem>
                        <SelectItem value="No">No</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Dynamic COA Certificate Upload Section */}
                {formData.coa_cert_attached === "Yes" && (
                  <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 space-y-3 mt-2 text-left">
                    <div className="flex items-center justify-between">
                      <h5 className="text-xs font-bold text-slate-700 uppercase tracking-wide flex items-center gap-1.5">
                        <Award className="w-4 h-4 text-red-600" /> Attached COA Certificates
                      </h5>
                      <div>
                        <input 
                          type="file" 
                          id="coa-document-upload" 
                          className="hidden" 
                          onChange={handleCoaUpload}
                        />
                        <label 
                          htmlFor="coa-document-upload"
                          className="cursor-pointer text-xs font-bold text-red-700 hover:text-red-900 bg-red-50 hover:bg-red-100 border border-red-200 px-3 py-1.5 rounded-lg flex items-center gap-1.5 transition-colors"
                        >
                          <Upload className="w-3.5 h-3.5" /> Upload COA Cert
                        </label>
                      </div>
                    </div>

                    {!formData.coa_documents || formData.coa_documents.length === 0 ? (
                      <p className="text-xs text-slate-400 italic">No COA certificates uploaded yet.</p>
                    ) : (
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-2">
                        {formData.coa_documents.map(doc => (
                          <div key={doc.id} className="p-2.5 bg-white border border-slate-200 rounded-lg flex items-center justify-between gap-2 overflow-hidden shadow-sm">
                            <div className="flex items-center gap-2 overflow-hidden">
                              <span className="w-1.5 h-1.5 bg-red-500 rounded-full shrink-0" />
                              <span className="text-xs font-semibold text-slate-800 truncate" title={doc.name}>
                                {doc.name}
                              </span>
                              <span className="text-[10px] text-slate-400 shrink-0">
                                ({doc.size})
                              </span>
                            </div>
                            <div className="flex items-center gap-1.5 shrink-0">
                              <a 
                                href={doc.url} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="text-xs font-bold text-slate-700 hover:text-slate-900 bg-slate-50 hover:bg-slate-100 border border-slate-200 px-2 py-1 rounded shadow-sm"
                              >
                                View
                              </a>
                              <Button 
                                type="button" 
                                variant="ghost" 
                                size="sm" 
                                className="h-7 w-7 p-0 text-red-650 hover:text-red-800 hover:bg-red-50"
                                onClick={() => removeCoaDocument(doc.id)}
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                <div className="border-t border-slate-100 pt-4 space-y-4">
                  <h4 className="text-xs font-bold text-slate-700 uppercase tracking-wide">Internal Lineage Tree Mapping (COA Registry)</h4>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-1.5">
                      <Label className="text-xs font-semibold text-slate-700">Mother's Details</Label>
                      <Input 
                        value={formData.mum_name} 
                        onChange={e => handleFieldChange("mum_name", e.target.value)} 
                        placeholder="Mother's Full Name & Mob"
                        className="text-sm rounded-lg"
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs font-semibold text-slate-700">Father's Details</Label>
                      <Input 
                        value={formData.dad_name} 
                        onChange={e => handleFieldChange("dad_name", e.target.value)} 
                        placeholder="Father's Full Name & Mob"
                        className="text-sm rounded-lg"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 border-t border-slate-50 pt-3">
                    <div className="space-y-1.5">
                      <Label className="text-xs font-semibold text-slate-700">Maternal Grandmother</Label>
                      <Input 
                        value={formData.maternal_grandma} 
                        onChange={e => handleFieldChange("maternal_grandma", e.target.value)} 
                        placeholder="Grandmother's Name"
                        className="text-sm rounded-lg"
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs font-semibold text-slate-700">Maternal Grandfather</Label>
                      <Input 
                        value={formData.maternal_grandpa} 
                        onChange={e => handleFieldChange("maternal_grandpa", e.target.value)} 
                        placeholder="Grandfather's Name"
                        className="text-sm rounded-lg"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 border-t border-slate-50 pt-3">
                    <div className="space-y-1.5">
                      <Label className="text-xs font-semibold text-slate-700">Paternal Grandmother</Label>
                      <Input 
                        value={formData.paternal_grandma} 
                        onChange={e => handleFieldChange("paternal_grandma", e.target.value)} 
                        placeholder="Grandmother's Name"
                        className="text-sm rounded-lg"
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs font-semibold text-slate-700">Paternal Grandfather</Label>
                      <Input 
                        value={formData.paternal_grandpa} 
                        onChange={e => handleFieldChange("paternal_grandpa", e.target.value)} 
                        placeholder="Grandfather's Name"
                        className="text-sm rounded-lg"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 border-t border-slate-100 pt-4">
                    <div className="space-y-1.5">
                      <Label className="text-xs font-semibold text-slate-700">Mob Connection</Label>
                      <Input 
                        value={formData.mob_name} 
                        onChange={e => handleFieldChange("mob_name", e.target.value)} 
                        placeholder="e.g. Gunditjmara"
                        className="text-sm rounded-lg"
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs font-semibold text-slate-700">ACCOs Involved</Label>
                      <Input 
                        value={formData.accos_involved} 
                        onChange={e => handleFieldChange("accos_involved", e.target.value)} 
                        placeholder="e.g. Winda-Mara"
                        className="text-sm rounded-lg"
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs font-semibold text-slate-700">Other Family Members</Label>
                      <Input 
                        value={formData.other_family_members} 
                        onChange={e => handleFieldChange("other_family_members", e.target.value)} 
                        placeholder="Aunties, Uncles, Siblings..."
                        className="text-sm rounded-lg"
                      />
                    </div>
                  </div>
                </div>

              </div>
            )}

            {/* TAB: Health & Financial factors */}
            {activeTab === "health" && (
              <div className="space-y-6">
                <h3 className="text-sm font-bold text-slate-950 uppercase tracking-wider flex items-center gap-2 border-b border-slate-100 pb-2">
                  <Heart className="w-4.5 h-4.5 text-red-600" />
                  Health &amp; Financial Disadvantage Factors
                </h3>

                <div className="space-y-4">
                  <div className="space-y-1.5">
                    <Label className="text-xs font-semibold text-slate-700">Physical Health Factors</Label>
                    <Input 
                      value={formData.health_physical} 
                      onChange={e => handleFieldChange("health_physical", e.target.value)} 
                      placeholder="e.g. Chronic illness, accessibility requirements..."
                      className="text-sm rounded-lg"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs font-semibold text-slate-700">Intellectual / Cognitive Disability Details</Label>
                    <Input 
                      value={formData.health_intellectual} 
                      onChange={e => handleFieldChange("health_intellectual", e.target.value)} 
                      placeholder="e.g. Learning difficulties, memory issues..."
                      className="text-sm rounded-lg"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs font-semibold text-slate-700">Mental Health Issues</Label>
                    <Input 
                      value={formData.health_mental} 
                      onChange={e => handleFieldChange("health_mental", e.target.value)} 
                      placeholder="e.g. Anxiety, depression, psychiatric reports..."
                      className="text-sm rounded-lg"
                    />
                  </div>
                </div>

                <div className="border-t border-slate-100 pt-4 space-y-4">
                  <h4 className="text-xs font-bold text-slate-700 uppercase tracking-wide flex items-center gap-1.5">
                    <DollarSign className="w-4 h-4 text-red-600" /> Financial Circumstances
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="space-y-1.5">
                      <Label className="text-xs font-semibold text-slate-700">Income Bracket</Label>
                      <Select value={formData.income_level} onValueChange={v => handleFieldChange("income_level", v)}>
                        <SelectTrigger className="text-sm rounded-lg">
                          <SelectValue placeholder="Select Bracket" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="< $65,000 p/a">Under $65,000 per annum</SelectItem>
                          <SelectItem value=">= $65,000 p/a">Equal to or over $65,000 per annum</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs font-semibold text-slate-700">Centrelink Benefit Type</Label>
                      <Select value={formData.centrelink_benefit} onValueChange={v => handleFieldChange("centrelink_benefit", v)}>
                        <SelectTrigger className="text-sm rounded-lg">
                          <SelectValue placeholder="Select Benefit" />
                        </SelectTrigger>
                        <SelectContent>
                          {CENTRELINK_BENEFITS.map(benefit => (
                            <SelectItem key={benefit} value={benefit}>{benefit}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs font-semibold text-slate-700">Financial CRN</Label>
                      <Input 
                        value={formData.financial_crn} 
                        onChange={e => handleFieldChange("financial_crn", e.target.value)} 
                        placeholder="Centrelink CRN"
                        className="text-sm font-mono rounded-lg"
                      />
                    </div>
                  </div>
                </div>

              </div>
            )}

            {/* TAB: Court Details */}
            {activeTab === "court" && (
              <div className="space-y-6">
                <h3 className="text-sm font-bold text-slate-950 uppercase tracking-wider flex items-center gap-2 border-b border-slate-100 pb-2">
                  <Calendar className="w-4.5 h-4.5 text-red-600" />
                  Court &amp; Adjournment Settings
                </h3>

                <div className="p-3 bg-blue-50/70 border border-blue-200 rounded-xl text-sm text-blue-800 space-y-1.5 text-left">
                  <p className="font-bold text-xs">Search for Court Listings &amp; Cases:</p>
                  <ul className="space-y-1 text-xs list-disc pl-4">
                    <li>
                      <a href="https://cmsportal.courts.vic.gov.au/?q=hearings" target="_blank" rel="noopener noreferrer" className="underline hover:text-blue-600 font-semibold">
                        Magistrates' Court Daily Lists – Case Search
                      </a>
                    </li>
                    <li>
                      <a href="https://courtconnect.countycourt.vic.gov.au" target="_blank" rel="noopener noreferrer" className="underline hover:text-blue-600 font-semibold">
                        County Court &amp; Supreme Court – Court Connect Portal
                      </a>
                    </li>
                  </ul>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div className="space-y-1.5">
                    <Label className="text-xs font-semibold text-slate-700">Court Date</Label>
                    <Input 
                      type="date" 
                      value={formData.court_date} 
                      onChange={e => handleFieldChange("court_date", e.target.value)} 
                      className="text-sm rounded-lg"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs font-semibold text-slate-700">Court Jurisdiction</Label>
                    <Select 
                      value={formData.court_type} 
                      onValueChange={v => {
                        handleFieldChange("court_type", v);
                        handleFieldChange("court_location", "");
                        handleFieldChange("hearing_type", "");
                      }}
                    >
                      <SelectTrigger className="text-sm rounded-lg">
                        <SelectValue placeholder="Select Court" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Children’s">Children’s Court</SelectItem>
                        <SelectItem value="Magistrates'">Magistrates’ Court</SelectItem>
                        <SelectItem value="County">County Court</SelectItem>
                        <SelectItem value="Supreme">Supreme Court</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs font-semibold text-slate-700">Court Location</Label>
                    <Select 
                      value={formData.court_location} 
                      onValueChange={v => handleFieldChange("court_location", v)}
                      disabled={!formData.court_type}
                    >
                      <SelectTrigger className="text-sm rounded-lg">
                        <SelectValue placeholder={formData.court_type ? "Select Location" : "Select jurisdiction first"} />
                      </SelectTrigger>
                      <SelectContent>
                        {formData.court_type && getCourtLocationOptions(formData.court_type).map(loc => (
                          <SelectItem key={loc} value={loc}>{loc}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs font-semibold text-slate-700">Hearing Type</Label>
                    <Select 
                      value={formData.hearing_type} 
                      onValueChange={v => handleFieldChange("hearing_type", v)}
                      disabled={!formData.court_type}
                    >
                      <SelectTrigger className="text-sm rounded-lg">
                        <SelectValue placeholder={formData.court_type ? "Select Hearing Type" : "Select jurisdiction first"} />
                      </SelectTrigger>
                      <SelectContent>
                        {formData.court_type && getHearingTypeOptions(formData.court_type).map(ht => (
                          <SelectItem key={ht} value={ht}>{ht}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 border-t border-slate-100 pt-4">
                  <div className="space-y-1.5">
                    <Label className="text-xs font-semibold text-slate-700">Adjourned Previously?</Label>
                    <Select value={formData.adjourned_previously} onValueChange={v => handleFieldChange("adjourned_previously", v)}>
                      <SelectTrigger className="text-sm rounded-lg">
                        <SelectValue placeholder="Select" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Yes">Yes</SelectItem>
                        <SelectItem value="No">No</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs font-semibold text-slate-700">Number of Adjournments</Label>
                    <Input 
                      type="number" 
                      value={formData.adjourned_times} 
                      onChange={e => handleFieldChange("adjourned_times", e.target.value)} 
                      disabled={formData.adjourned_previously === "No"}
                      className="text-sm rounded-lg font-mono"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs font-semibold text-slate-700">Client is currently on:</Label>
                    <Select value={formData.client_on_status} onValueChange={v => handleFieldChange("client_on_status", v)}>
                      <SelectTrigger className="text-sm rounded-lg">
                        <SelectValue placeholder="Status" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Summons">Summons</SelectItem>
                        <SelectItem value="Bail">Bail</SelectItem>
                        <SelectItem value="Remand">Remand</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="border-t border-slate-100 pt-4 space-y-4">
                  <h4 className="text-xs font-bold text-slate-700 uppercase tracking-wide">Adjournment Process Checks</h4>
                  
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="space-y-1.5">
                      <Label className="text-xs font-semibold text-slate-700">Adjournment Required?</Label>
                      <Select value={formData.adjournment_required} onValueChange={v => handleFieldChange("adjournment_required", v)}>
                        <SelectTrigger className="text-sm rounded-lg">
                          <SelectValue placeholder="Select" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Yes">Yes</SelectItem>
                          <SelectItem value="No">No</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs font-semibold text-slate-700">Is Court Date a {orgShort} Date?</Label>
                      <Select value={formData.vals_date} onValueChange={v => handleFieldChange("vals_date", v)}>
                        <SelectTrigger className="text-sm rounded-lg">
                          <SelectValue placeholder="Select" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Yes">Yes</SelectItem>
                          <SelectItem value="No">No</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs font-semibold text-slate-700">Client Consent to Adjourn?</Label>
                      <Select value={formData.consent_to_adjourn} onValueChange={v => handleFieldChange("consent_to_adjourn", v)}>
                        <SelectTrigger className="text-sm rounded-lg">
                          <SelectValue placeholder="Select" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Yes">Yes</SelectItem>
                          <SelectItem value="No">No</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs font-semibold text-slate-700">Sent Adjournment Off?</Label>
                      <Select value={formData.sent_adjournment} onValueChange={v => handleFieldChange("sent_adjournment", v)}>
                        <SelectTrigger className="text-sm rounded-lg">
                          <SelectValue placeholder="Select" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Yes">Yes</SelectItem>
                          <SelectItem value="No">No</SelectItem>
                          <SelectItem value="N/A">N/A</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    {formData.adjournment_required === "Yes" && (
                      <div className="space-y-1.5 sm:col-span-2 md:col-span-1">
                        <Label className="text-xs font-semibold text-slate-700">Email Court Coordinator?</Label>
                        <Select value={formData.email_court} onValueChange={v => handleFieldChange("email_court", v)}>
                          <SelectTrigger className="text-sm rounded-lg">
                            <SelectValue placeholder="Select" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Yes">Yes</SelectItem>
                            <SelectItem value="No">No</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                  </div>

                  <div className="space-y-1.5">
                    <Label className="text-xs font-semibold text-slate-700">Court Adjournment Notes</Label>
                    <Textarea 
                      value={formData.court_notes} 
                      onChange={e => handleFieldChange("court_notes", e.target.value)} 
                      placeholder="Add details regarding adjournment submissions, files attached, dates etc."
                      rows={3}
                      className="text-xs rounded-lg"
                    />
                  </div>

                  {formData.adjournment_required === "Yes" && formData.email_court === "Yes" && (
                    <div className="bg-slate-50 rounded-xl p-4 border border-slate-200 mt-4 space-y-4 print:hidden">
                      <div className="flex items-center justify-between border-b border-slate-200 pb-2">
                        <div className="flex items-center gap-2">
                          <Mail className="w-4 h-4 text-red-600" />
                          <h5 className="text-xs font-bold text-slate-800 uppercase tracking-wide">Draft Adjournment Request Email</h5>
                        </div>
                        <Button 
                          type="button" 
                          variant="ghost" 
                          size="sm" 
                          onClick={handleRegenerateEmail}
                          className="h-7 text-xs text-red-600 hover:text-red-700 hover:bg-red-50 flex items-center gap-1 px-2 rounded-md"
                        >
                          <RotateCcw className="w-3 h-3" />
                          Reset Draft
                        </Button>
                      </div>

                      <div className="space-y-3">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          <div className="space-y-1">
                            <Label className="text-xs font-semibold text-slate-600">To</Label>
                            <Input 
                              type="email" 
                              value={formData.email_to || ""} 
                              onChange={e => handleFieldChange("email_to", e.target.value)} 
                              placeholder="coordinator@courts.vic.gov.au"
                              className="text-sm rounded-lg bg-white"
                            />
                          </div>
                          <div className="space-y-1">
                            <Label className="text-xs font-semibold text-slate-600">Subject</Label>
                            <Input 
                              type="text" 
                              value={formData.email_subject || ""} 
                              onChange={e => handleFieldChange("email_subject", e.target.value)} 
                              placeholder="Email Subject"
                              className="text-sm rounded-lg bg-white"
                            />
                          </div>
                        </div>

                        <div className="space-y-1">
                          <Label className="text-xs font-semibold text-slate-600">Message Body</Label>
                          <Textarea 
                            value={formData.email_body || ""} 
                            onChange={e => handleFieldChange("email_body", e.target.value)} 
                            placeholder="Type email body here..."
                            rows={12}
                            className="text-sm rounded-lg bg-white font-sans whitespace-pre-wrap"
                          />
                        </div>

                        <div className="flex justify-end gap-2 pt-2">
                          <Button 
                            type="button" 
                            variant="outline" 
                            size="sm"
                            onClick={() => {
                              navigator.clipboard.writeText(formData.email_body || "");
                              toast.success("Email body copied to clipboard.");
                            }}
                            className="text-xs flex items-center gap-1.5 h-8"
                          >
                            <FileText className="w-3.5 h-3.5" />
                            Copy Content
                          </Button>
                          <Button 
                            type="button" 
                            size="sm"
                            onClick={async () => {
                              const adjSubject = formData.email_subject || `Adjournment Request - ${formData.first_name || ""} ${formData.surname || ""}`;
                              await logIntakeSubmissionToOutbox(adjSubject, formData.email_body || "");
                              
                              const mailtoUrl = `mailto:${encodeURIComponent(formData.email_to || "")}?subject=${encodeURIComponent(formData.email_subject || "")}&body=${encodeURIComponent(formData.email_body || "")}`;
                              const link = document.createElement("a");
                              link.href = mailtoUrl;
                              link.click();
                            }}
                            className="text-xs bg-red-600 hover:bg-red-700 text-white flex items-center gap-1.5 h-8"
                          >
                            <Mail className="w-3.5 h-3.5" />
                            Send Email
                          </Button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

              </div>
            )}

            {/* TAB: Charges & Co-accused */}
            {activeTab === "charges" && (
              <div className="space-y-6">
                
                {/* Charge Section Header */}
                <div className="flex items-center justify-between border-b border-slate-100 pb-2">
                  <h3 className="text-sm font-bold text-slate-950 uppercase tracking-wider flex items-center gap-2">
                    <FileText className="w-4.5 h-4.5 text-red-600" />
                    Charges Logged (Police Brief)
                  </h3>
                  <Button 
                    type="button" 
                    onClick={addCharge} 
                    size="sm" 
                    className="bg-red-50 text-red-700 border border-red-200 hover:bg-red-100 gap-1 rounded-xl text-xs"
                  >
                    <Plus className="w-3.5 h-3.5" /> Add Charge Item
                  </Button>
                </div>

                {formData.charges_list.length === 0 ? (
                  <div className="p-8 text-center bg-slate-50 border border-dashed border-slate-200 rounded-xl">
                    <AlertTriangle className="w-6 h-6 text-slate-400 mx-auto mb-2" />
                    <p className="text-xs font-semibold text-slate-700">No charges logged</p>
                    <p className="text-[11px] text-slate-400 mt-0.5">Click "Add Charge Item" to add police charge sheets.</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {formData.charges_list.map((charge, idx) => (
                      <div key={charge.id} className="p-4 bg-slate-50/50 border border-slate-200 rounded-xl space-y-3 relative">
                        <div className="flex items-center justify-between pb-2 border-b border-slate-100">
                          <span className="text-xs font-bold text-red-800 bg-red-100 px-2 py-0.5 rounded-lg">Charge #{idx + 1}</span>
                          <Button 
                            type="button" 
                            variant="ghost" 
                            size="sm" 
                            onClick={() => removeCharge(idx)} 
                            className="text-xs h-7 text-rose-600 hover:text-rose-800 hover:bg-rose-50"
                          >
                            <Trash2 className="w-3.5 h-3.5 mr-0.5" /> Delete
                          </Button>
                        </div>
                        
                        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                          <div className="space-y-1.5">
                            <Label className="text-xs font-semibold text-slate-700 font-sans">Offence Group</Label>
                            <Select 
                              value={withoutOffenceCode(charge.offence_group)} 
                              onValueChange={v => {
                                handleChargeChange(idx, "offence_group", v);
                                handleChargeChange(idx, "charge_title", "");
                              }}
                            >
                              <SelectTrigger className="text-sm rounded-lg bg-white">
                                <SelectValue placeholder="Select Group" />
                              </SelectTrigger>
                              <SelectContent>
                                {OFFENCE_HEADINGS.map(group => (
                                  <SelectItem key={group} value={group}>{group}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="space-y-1.5">
                            <Label className="text-xs font-semibold text-slate-700 font-sans">Offence Description</Label>
                            <Select 
                              value={charge.charge_title} 
                              onValueChange={v => handleChargeChange(idx, "charge_title", v)}
                              disabled={!charge.offence_group}
                            >
                              <SelectTrigger className="text-sm rounded-lg bg-white">
                                <SelectValue placeholder={charge.offence_group ? "Select Description" : "Select group first"} />
                              </SelectTrigger>
                              <SelectContent>
                                {chargeOptions(charge).map(desc => (
                                  <SelectItem key={desc} value={desc}>{desc}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="space-y-1.5">
                            <Label className="text-xs font-semibold text-slate-700">Informant (Officer Name)</Label>
                            <Input 
                              value={charge.informant} 
                              onChange={e => handleChargeChange(idx, "informant", e.target.value)} 
                              placeholder="Officer Name / Rank"
                              className="text-sm rounded-lg bg-white"
                            />
                          </div>
                          <div className="space-y-1.5">
                            <Label className="text-xs font-semibold text-slate-700">VP Number (Ref #)</Label>
                            <Input 
                              value={charge.vp_number} 
                              onChange={e => handleChargeChange(idx, "vp_number", e.target.value)} 
                              placeholder="e.g. VP-8839201"
                              className="text-sm font-mono rounded-lg bg-white"
                            />
                          </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div className="space-y-1.5">
                            <Label className="text-xs font-semibold text-slate-700">Police Station</Label>
                            <Select 
                              value={charge.station} 
                              onValueChange={v => handleChargeChange(idx, "station", v)}
                            >
                              <SelectTrigger className="text-sm rounded-lg bg-white">
                                <SelectValue placeholder="Select Police Station" />
                              </SelectTrigger>
                              <SelectContent>
                                {POLICE_STATIONS.map(s => (
                                  <SelectItem key={s} value={s}>{s}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          
                          <div className="flex flex-col justify-end">
                            <div className="bg-slate-100/50 p-2.5 rounded-lg border border-slate-200/50 flex flex-col sm:flex-row sm:items-center justify-between gap-3 h-[42px]">
                              <div className="flex items-center gap-2 overflow-hidden">
                                <Label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider shrink-0">Police Brief:</Label>
                                {charge.police_brief ? (
                                  <div className="flex items-center gap-1.5 bg-red-100 text-red-800 text-xs px-2 py-0.5 rounded font-semibold overflow-hidden">
                                    <Paperclip className="w-3 h-3 text-red-600 shrink-0" />
                                    <span className="truncate max-w-[120px] text-[11px]" title={charge.police_brief.name}>{charge.police_brief.name}</span>
                                    <span className="text-[9px] text-red-600/80 font-normal shrink-0">({charge.police_brief.size})</span>
                                  </div>
                                ) : (
                                  <span className="text-[11px] text-slate-400 italic">No brief attached</span>
                                )}
                              </div>
                              
                              <div className="flex items-center gap-1.5 shrink-0">
                                {charge.police_brief ? (
                                  <>
                                    <a 
                                      href={charge.police_brief.url} 
                                      target="_blank" 
                                      rel="noopener noreferrer"
                                      className="text-[11px] font-bold text-slate-700 hover:text-slate-900 bg-white hover:bg-slate-100 border border-slate-200 px-2 py-1 rounded flex items-center gap-0.5 shadow-sm"
                                    >
                                      <Eye className="w-3 h-3" /> View
                                    </a>
                                    <Button 
                                      type="button" 
                                      variant="ghost" 
                                      size="sm" 
                                      onClick={() => removeBrief(idx)}
                                      className="text-rose-600 hover:text-rose-800 hover:bg-rose-50 h-7 px-1.5 text-[11px]"
                                    >
                                      <Trash2 className="w-3 h-3 mr-0.5" /> Delete
                                    </Button>
                                  </>
                                ) : (
                                  <>
                                    <input 
                                      type="file" 
                                      id={`brief-upload-${idx}`} 
                                      className="hidden" 
                                      onChange={e => handleBriefUpload(idx, e)}
                                    />
                                    <label 
                                      htmlFor={`brief-upload-${idx}`}
                                      className="cursor-pointer text-[11px] font-bold text-red-700 hover:text-red-900 bg-red-50 hover:bg-red-100 border border-red-200 px-2.5 py-1 rounded flex items-center gap-1 transition-colors"
                                    >
                                      <Upload className="w-3 h-3" /> Attach Brief
                                    </label>
                                  </>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Co-Accused */}
                <div className="border-t border-slate-100 pt-4 space-y-3">
                  <h4 className="text-xs font-bold text-slate-700 uppercase tracking-wide">Other Party / Co-Accused</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-1.5">
                      <Label className="text-xs font-semibold text-slate-700">Co-Accused Full Name</Label>
                      <Input 
                        value={formData.co_accused_name} 
                        onChange={e => handleFieldChange("co_accused_name", e.target.value)} 
                        placeholder="Co-Accused Name"
                        className="text-sm rounded-lg"
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs font-semibold text-slate-700">Co-Accused DOB</Label>
                      <Input 
                        type="date" 
                        value={formData.co_accused_dob} 
                        onChange={e => handleFieldChange("co_accused_dob", e.target.value)} 
                        className="text-sm rounded-lg"
                      />
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs font-semibold text-slate-700">Co-Accused / Conflict Notes</Label>
                    <Input 
                      value={formData.co_accused_notes} 
                      onChange={e => handleFieldChange("co_accused_notes", e.target.value)} 
                      placeholder="Family relation, associated court files, warnings..."
                      className="text-sm rounded-lg"
                    />
                  </div>
                </div>

              </div>
            )}

            {/* TAB: Conflicts & Checks */}
            {activeTab === "checks" && (
              <div className="space-y-6">
                
                {/* Conflict Card */}
                <div className="space-y-4">
                  <h3 className="text-sm font-bold text-slate-950 uppercase tracking-wider flex items-center gap-2 border-b border-slate-100 pb-2">
                    <ShieldCheck className="w-4.5 h-4.5 text-red-600" />
                    Conflict Database Verification
                  </h3>
                  
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="space-y-1.5">
                      <Label className="text-xs font-semibold text-slate-700">Checked By</Label>
                      <Input 
                        value={formData.conflict_checked_by} 
                        onChange={e => handleFieldChange("conflict_checked_by", e.target.value)} 
                        placeholder="Staff Name"
                        className="text-sm rounded-lg"
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs font-semibold text-slate-700">Date Completed</Label>
                      <Input 
                        type="date" 
                        value={formData.conflict_check_date} 
                        onChange={e => handleFieldChange("conflict_check_date", e.target.value)} 
                        className="text-sm rounded-lg"
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs font-semibold text-slate-700">Conflict Found?</Label>
                      <Select value={formData.conflict_status} onValueChange={v => handleFieldChange("conflict_status", v)}>
                        <SelectTrigger className="text-sm rounded-lg">
                          <SelectValue placeholder="Select" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Yes">Yes (Conflict exists)</SelectItem>
                          <SelectItem value="No">No (Clear)</SelectItem>
                          <SelectItem value="N/A">N/A</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-1.5">
                      <Label className="text-xs font-semibold text-slate-700">If N/A, Reason</Label>
                      <Input 
                        value={formData.conflict_na_reason} 
                        onChange={e => handleFieldChange("conflict_na_reason", e.target.value)} 
                        placeholder="Reason why conflict check was bypassed"
                        disabled={formData.conflict_status !== "N/A"}
                        className="text-sm rounded-lg"
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs font-semibold text-slate-700">Conflict Details</Label>
                      <Input 
                        value={formData.conflict_details} 
                        onChange={e => handleFieldChange("conflict_details", e.target.value)} 
                        placeholder="Names of conflicted parties, files..."
                        disabled={formData.conflict_status !== "Yes"}
                        className="text-sm rounded-lg"
                      />
                    </div>
                  </div>
                </div>

                {/* Link to VALS Legal Aid Form */}
                <div className="space-y-3">
                  <div className="bg-blue-50/50 border border-blue-100 rounded-xl p-3.5 flex flex-col sm:flex-row sm:items-center justify-between gap-3 text-left">
                    <div className="space-y-0.5">
                      <h4 className="text-xs font-bold text-slate-800">Victoria Legal Aid Application</h4>
                      <p className="text-[11px] text-slate-500">If the conflict check is clear, prepare or review the VLA Legal Aid Application form.</p>
                    </div>
                    <a 
                      href={createPageUrl("LegalAidForm")}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="cursor-pointer text-xs font-bold text-blue-700 hover:text-blue-900 bg-blue-50 hover:bg-blue-100 border border-blue-200 px-3.5 py-2 rounded-xl flex items-center gap-1.5 transition-colors self-start sm:self-center"
                    >
                      <FileText className="w-3.5 h-3.5 text-blue-600" /> Open VLA Legal Aid Form
                    </a>
                  </div>

                  {/* Completed VLA List Scanner */}
                  {completedVlas.length > 0 && (
                    <div className="bg-slate-50 border border-slate-200/60 rounded-xl p-3 space-y-2 text-left print:hidden">
                      <h5 className="text-[11px] font-bold text-slate-700 uppercase tracking-wider flex items-center gap-1">
                        <Paperclip className="w-3.5 h-3.5 text-slate-500" /> Completed / Draft VLA Forms Found in Session
                      </h5>
                      <div className="grid grid-cols-1 gap-2">
                        {completedVlas.map(vla => {
                          const isAttached = formData.coa_documents.some(d => d.id === `vla-attach-${vla.id}`);
                          return (
                            <div key={vla.id} className="flex items-center justify-between bg-white border border-slate-100 p-2 rounded-lg">
                              <div className="flex items-center gap-1.5">
                                <FileText className="w-4 h-4 text-blue-600" />
                                <div>
                                  <div className="text-xs font-bold text-slate-800">{vla.applicant_name}&apos;s VLA Form</div>
                                  <div className="text-[9px] text-slate-500">
                                    {vla.type === 'VLA_DRAFT' ? `Draft Step ${vla.current_step + 1}/10` : 'Submitted/Finalized'} · Completed: {new Date(vla.completed_at).toLocaleDateString()}
                                  </div>
                                </div>
                              </div>
                              <Button
                                type="button"
                                size="sm"
                                variant={isAttached ? "ghost" : "outline"}
                                disabled={isAttached}
                                onClick={() => {
                                  const newDoc = {
                                    id: `vla-attach-${vla.id}`,
                                    name: `VLA_Application_${vla.applicant_name.replace(/\s+/g, "_")}.pdf`,
                                    size: "420 KB",
                                    url: createPageUrl("LegalAidForm") + `?id=${vla.id}`
                                  };
                                  setFormData(prev => ({
                                    ...prev,
                                    coa_documents: [...(prev.coa_documents || []), newDoc]
                                  }));
                                  toast.success(`Attached completed VLA form: ${vla.applicant_name}`);
                                }}
                                className="text-[10px] font-bold h-7 px-2.5 rounded-lg border-slate-200 hover:bg-slate-50 text-blue-600"
                              >
                                {isAttached ? "✓ Attached" : "Attach to Intake"}
                              </Button>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>

                {/* Additional Representation Checks */}
                <div className="border-t border-slate-100 pt-4 space-y-4">
                  <h4 className="text-xs font-bold text-slate-700 uppercase tracking-wide">Client Authority &amp; Representation Checks</h4>
                  
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="space-y-1.5 col-span-1 md:col-span-3 border border-slate-100 p-3.5 bg-slate-50/50 rounded-xl space-y-3">
                      <div className="flex items-center justify-between gap-4">
                        <Label className="text-xs font-semibold text-slate-700">Client Authority Document Completed?</Label>
                        <div className="flex items-center gap-2">
                          <a 
                            href={createPageUrl("VALSAuthorityToAct")}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-[10px] text-red-600 hover:text-red-800 font-bold underline flex items-center gap-0.5"
                          >
                            <FileText className="w-3 h-3" /> Fill Digital ATA Form
                          </a>
                        </div>
                      </div>
                      <Select value={formData.client_authority_done} onValueChange={v => handleFieldChange("client_authority_done", v)}>
                        <SelectTrigger className="text-sm rounded-lg bg-white">
                          <SelectValue placeholder="Select" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Yes">Yes (Authority Obtained)</SelectItem>
                          <SelectItem value="No">No</SelectItem>
                        </SelectContent>
                      </Select>

                      {/* Completed ATA Forms Scanner */}
                      {completedAtas.length > 0 && (
                        <div className="bg-white border border-slate-200/60 rounded-lg p-2.5 space-y-2 mt-2">
                          <h5 className="text-[10px] font-bold text-slate-700 uppercase tracking-wider flex items-center gap-1">
                            <Paperclip className="w-3.5 h-3.5 text-slate-500" /> Completed Digital ATA Forms Found
                          </h5>
                          <div className="grid grid-cols-1 gap-2">
                            {completedAtas.map(ata => {
                              const isAttached = formData.coa_documents.some(d => d.id === `ata-attach-${ata.id}`);
                              return (
                                <div key={ata.id} className="flex items-center justify-between bg-slate-50/50 border border-slate-100 p-2 rounded-lg">
                                  <div className="flex items-center gap-1.5">
                                    <FileText className="w-4 h-4 text-red-600" />
                                    <div>
                                      <div className="text-xs font-bold text-slate-800">{ata.clientName}&apos;s Authority Form</div>
                                      <div className="text-[9px] text-slate-500">Completed: {ata.date}</div>
                                    </div>
                                  </div>
                                  <Button
                                    type="button"
                                    size="sm"
                                    variant={isAttached ? "ghost" : "outline"}
                                    disabled={isAttached}
                                    onClick={() => {
                                      const newDoc = {
                                        id: `ata-attach-${ata.id}`,
                                        name: `VALS_Authority_To_Act_${ata.clientName.replace(/\s+/g, "_")}.pdf`,
                                        size: "192 KB",
                                        url: createPageUrl("VALSAuthorityToAct") + `?id=${ata.id}`
                                      };
                                      setFormData(prev => ({
                                        ...prev,
                                        coa_documents: [...(prev.coa_documents || []), newDoc],
                                        client_authority_done: "Yes"
                                      }));
                                      toast.success(`Attached completed ATA: ${ata.clientName}`);
                                    }}
                                    className="text-[10px] font-bold h-7 px-2.5 rounded-lg border-slate-200 hover:bg-slate-50 text-red-600"
                                  >
                                    {isAttached ? "✓ Attached" : "Attach to Intake"}
                                  </Button>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs font-semibold text-slate-700">Client Already Has a Lawyer?</Label>
                      <Select value={formData.already_has_lawyer} onValueChange={v => handleFieldChange("already_has_lawyer", v)}>
                        <SelectTrigger className="text-sm rounded-lg">
                          <SelectValue placeholder="Select" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Yes">Yes</SelectItem>
                          <SelectItem value="No">No</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs font-semibold text-slate-700">Existing Lawyer's Name</Label>
                      <Input 
                        value={formData.existing_lawyer_name} 
                        onChange={e => handleFieldChange("existing_lawyer_name", e.target.value)} 
                        placeholder="Existing Lawyer / Firm"
                        disabled={formData.already_has_lawyer === "No"}
                        className="text-sm rounded-lg"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-1.5">
                      <Label className="text-xs font-semibold text-slate-700">* If in custody * Applied for Bail?</Label>
                      <Select value={formData.applied_for_bail} onValueChange={v => handleFieldChange("applied_for_bail", v)}>
                        <SelectTrigger className="text-sm rounded-lg">
                          <SelectValue placeholder="Select" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Yes">Yes</SelectItem>
                          <SelectItem value="No">No</SelectItem>
                          <SelectItem value="N/A">N/A (Not in custody)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs font-semibold text-slate-700">Consent to referral if {orgShort} cannot assist?</Label>
                      <Select value={formData.consent_to_referral} onValueChange={v => handleFieldChange("consent_to_referral", v)}>
                        <SelectTrigger className="text-sm rounded-lg">
                          <SelectValue placeholder="Select" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Yes">Yes</SelectItem>
                          <SelectItem value="No">No</SelectItem>
                          <SelectItem value="N/A">N/A</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>

                {/* Advice & Instructions */}
                <div className="border-t border-slate-100 pt-4 space-y-2">
                  <h4 className="text-xs font-bold text-slate-700 uppercase tracking-wide flex items-center gap-1">
                    <FileEdit className="w-4 h-4 text-red-600" /> Initial Advice / Instructions
                  </h4>
                  <Textarea 
                    value={formData.advice_instructions} 
                    onChange={e => handleFieldChange("advice_instructions", e.target.value)} 
                    placeholder="Log summary of initial instructions given by client and legal advice provided..."
                    rows={5}
                    className="text-xs"
                  />
                </div>

                {/* Actionstep Integration Card */}
                <div className="border-t border-slate-200 pt-6 space-y-4 print:hidden text-left">
                  <h4 className="text-xs font-bold text-slate-700 uppercase tracking-wide flex items-center gap-1.5">
                    <BriefcaseBusiness className="w-4 h-4 text-red-600" />
                    Save to Actionstep
                  </h4>
                  <div className="bg-red-50/60 border border-red-100 rounded-2xl p-4 flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <p className="text-[11px] text-red-700 leading-normal max-w-xl">
                      Save a file note for this intake form to the linked Actionstep matter.
                    </p>
                    <ActionstepSavePanel
                      noteText={buildIntakeNoteText()}
                      formLabel="Criminal Intake Form"
                      accentColor="#8C1D18"
                      disabled={!formData.first_name && !formData.surname}
                    />
                  </div>
                </div>

              </div>
            )}

            {/* TAB: To Do List & Actions */}
            {activeTab === "todo" && (
              <div className="space-y-6">
                <h3 className="text-sm font-bold text-slate-950 uppercase tracking-wider flex items-center gap-2 border-b border-slate-100 pb-2">
                  <ListTodo className="w-4.5 h-4.5 text-red-600" />
                  Intake Action Checklist
                </h3>

                <div className="bg-slate-50 border border-slate-200 rounded-xl overflow-hidden">
                  <table className="w-full text-left border-collapse">
                    <thead>
                      <tr className="bg-slate-100 border-b border-slate-200">
                        <th className="p-3 text-xs font-bold text-slate-600 uppercase tracking-wider">Checklist Item</th>
                        <th className="p-3 text-xs font-bold text-slate-600 uppercase tracking-wider w-32">Completed?</th>
                        <th className="p-3 text-xs font-bold text-slate-600 uppercase tracking-wider w-40">Completed By</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-200">
                      {formData.checklist.map((todo, idx) => (
                        <tr key={todo.id} className="hover:bg-slate-100/50">
                          <td className="p-3 text-sm font-semibold text-slate-800">{todo.item}</td>
                          <td className="p-3">
                            <Select 
                              value={todo.completed} 
                              onValueChange={v => handleTodoChange(idx, "completed", v)}
                            >
                              <SelectTrigger className="h-8 text-xs font-semibold rounded-lg bg-white">
                                <SelectValue placeholder="Select" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="Yes">Yes</SelectItem>
                                <SelectItem value="No">No</SelectItem>
                              </SelectContent>
                            </Select>
                          </td>
                          <td className="p-3">
                            <Input 
                              value={todo.completed_by} 
                              onChange={e => handleTodoChange(idx, "completed_by", e.target.value)} 
                              placeholder="Initials"
                              className="h-8 text-xs font-semibold bg-white"
                            />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="border-t border-slate-100 pt-4 space-y-3">
                  <h4 className="text-xs font-bold text-slate-700 uppercase tracking-wide">Any Other Notes / Case Summary</h4>
                  <Textarea 
                    value={formData.other_notes} 
                    onChange={e => handleFieldChange("other_notes", e.target.value)} 
                    placeholder="Log extra casework summaries here..."
                    rows={4}
                    className="text-xs"
                  />
                </div>

                {/* Database entry Metadata */}
                <div className="border-t border-slate-200 pt-6 grid grid-cols-1 md:grid-cols-3 gap-6 bg-slate-50/50 p-4 rounded-xl border border-slate-100">
                  <div className="space-y-1.5">
                    <Label className="text-xs font-bold text-slate-700">DB Entry Date</Label>
                    <Input 
                      type="date" 
                      value={formData.db_entry_date} 
                      onChange={e => handleFieldChange("db_entry_date", e.target.value)} 
                      className="text-sm bg-white rounded-lg font-semibold"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs font-bold text-slate-700">Entered By</Label>
                    <Input 
                      value={formData.entered_by} 
                      onChange={e => handleFieldChange("entered_by", e.target.value)} 
                      placeholder="e.g. CrimSec"
                      className="text-sm bg-white rounded-lg font-semibold"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs font-bold text-slate-700">Form Completed By</Label>
                    <Input 
                      value={formData.form_completed_by} 
                      onChange={e => handleFieldChange("form_completed_by", e.target.value)} 
                      placeholder="Caseworker Name"
                      className="text-sm bg-white rounded-lg font-semibold"
                    />
                  </div>
                </div>

                {/* Sign-Off Block */}
                <div className="border-t border-slate-200 pt-6 grid grid-cols-1 md:grid-cols-2 gap-6 bg-slate-50/50 p-4 rounded-xl border border-slate-100">
                  <div className="space-y-1.5">
                    <Label className="text-xs font-bold text-slate-700">Solicitor / Paralegal Digital Signature</Label>
                    <Input 
                      value={formData.sig_name} 
                      onChange={e => handleFieldChange("sig_name", e.target.value)} 
                      placeholder="Type Full Name to Sign"
                      className="text-sm font-semibold bg-white rounded-lg"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs font-bold text-slate-700">Sign Date</Label>
                    <Input 
                      type="date" 
                      value={formData.sig_date} 
                      onChange={e => handleFieldChange("sig_date", e.target.value)} 
                      className="text-sm bg-white rounded-lg font-semibold"
                    />
                  </div>
                </div>

              </div>
            )}

            {/* Form actions (visible in tab views) */}
            <div className="flex flex-col sm:flex-row justify-between items-center gap-3 pt-6 border-t border-slate-100 print:hidden">
              <div className="flex gap-2 w-full sm:w-auto justify-start">
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => setShowPrefillModal(true)}
                  className="gap-2 rounded-xl text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700 font-semibold text-xs h-10"
                >
                  <RefreshCw className="w-4 h-4" /> Pre-fill Other Forms
                </Button>
                <ShareDraftButtons formData={formData} onImport={setFormData} brand={orgShort.toLowerCase()} formName="intake_form" />
                <CloudDraftsPanel formType="intake_form" formData={formData} onLoadDraft={setFormData} brand={orgShort.toLowerCase()} />
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={resetForm}
                  className="gap-2 rounded-xl text-slate-600 text-xs h-10"
                >
                  <RotateCcw className="w-4 h-4" /> Reset Form
                </Button>
              </div>
              
              <div className="flex gap-2 w-full sm:w-auto justify-end">
                {activeTab !== "client" && (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handlePrevTab}
                    className="gap-1.5 rounded-xl border-slate-200 text-slate-700 hover:bg-slate-50 font-semibold text-xs h-10"
                  >
                    <ChevronLeft className="w-4 h-4" /> Previous Section
                  </Button>
                )}
                
                {activeTab !== "todo" ? (
                  <Button
                    type="button"
                    onClick={handleNextTab}
                    className="bg-red-600 hover:bg-red-700 text-white gap-1.5 px-6 rounded-xl font-bold text-xs h-10"
                  >
                    Next Section <ChevronRight className="w-4 h-4" />
                  </Button>
                ) : (
                  <Button 
                    type="submit" 
                    disabled={isSaving}
                    className="bg-red-600 hover:bg-red-700 text-white gap-2 px-8 rounded-xl font-bold text-xs h-10"
                  >
                    {isSaving ? "Saving..." : (
                      <>
                        <CheckCircle2 className="w-4 h-4" /> Save Intake File
                      </>
                    )}
                  </Button>
                )}
              </div>
            </div>

          </form>

          {/* Footer info block */}
          <div className="mt-8 text-center text-slate-400 text-xs space-y-1 border-t border-slate-100 pt-6">
            <p>{orgName} · {orgAddress}</p>
            <p>Confidential Client Document · For Legal Representation Use Only</p>
          </div>

        </div>
      </div>

      {/* Dialog for Print / PDF Export */}
      <Dialog open={showExportDialog} onOpenChange={setShowExportDialog}>
        <DialogContent className="rounded-2xl max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-bold text-slate-900">Export {orgShort} Intake Form</DialogTitle>
          </DialogHeader>
          <p className="text-slate-500 text-xs leading-relaxed py-2">
            Your browser's print dialog will open. To save as a PDF, select <strong>"Save as PDF"</strong> as the destination.
          </p>
          <DialogFooter className="flex gap-2.5 justify-end pt-2">
            <Button variant="ghost" onClick={() => setShowExportDialog(false)} className="text-xs rounded-xl text-slate-500 hover:bg-slate-50">
              Cancel
            </Button>
            <Button 
              onClick={handlePrint} 
              className="bg-red-650 hover:bg-red-700 text-white text-xs font-bold rounded-xl px-5 flex items-center gap-1.5"
              style={{ backgroundColor: brandType === "lacw" ? "#9333ea" : brandType === "jbl" ? "#C29A5B" : "#8C1D18" }}
            >
              <Printer className="w-3.5 h-3.5" />
              <span>Print / Save as PDF</span>
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Actionstep dialog is now handled internally by ActionstepSavePanel */}

      {/* Pre-fill Other Forms Dialog */}
      <Dialog open={showPrefillModal} onOpenChange={setShowPrefillModal}>
        <DialogContent className="rounded-2xl max-w-md">
          <DialogHeader>
            <DialogTitle className="font-bold text-slate-900 flex items-center gap-1.5">
              <RefreshCw className="w-5 h-5 text-purple-600 animate-hover-spin" />
              Pre-fill Other Forms
            </DialogTitle>
            <DialogDescription className="text-xs text-slate-500">
              Select which attached forms to pre-populate with the current client demographics.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4 text-left">
            <p className="text-xs text-slate-600 leading-relaxed bg-purple-50/50 border border-purple-100 p-3 rounded-xl">
              This action will take the client details (Name, DOB, address, contact, and benefits) from this intake form and automatically pre-fill them when you open the selected forms in new tabs.
            </p>
            
            <div className="space-y-3">
              <label className="flex items-center gap-3 p-3 rounded-xl border border-slate-200 hover:bg-slate-50 cursor-pointer transition-colors">
                <input 
                  type="checkbox" 
                  checked={selectedPrefillForms.ata} 
                  onChange={(e) => setSelectedPrefillForms(prev => ({ ...prev, ata: e.target.checked }))}
                  className="rounded border-slate-300 text-red-600 focus:ring-red-500 h-4 w-4"
                />
                <div>
                  <div className="text-sm font-semibold text-slate-800">{orgShort} Authority to Act Form</div>
                  <div className="text-[10px] text-slate-500">Includes Client Name, DOB, and Address</div>
                </div>
              </label>

              <label className="flex items-center gap-3 p-3 rounded-xl border border-slate-200 hover:bg-slate-50 cursor-pointer transition-colors">
                <input 
                  type="checkbox" 
                  checked={selectedPrefillForms.vla} 
                  onChange={(e) => setSelectedPrefillForms(prev => ({ ...prev, vla: e.target.checked }))}
                  className="rounded border-slate-300 text-red-600 focus:ring-red-500 h-4 w-4"
                />
                <div>
                  <div className="text-sm font-semibold text-slate-800">Victoria Legal Aid Application Form</div>
                  <div className="text-[10px] text-slate-500">Includes Name, DOB, Contact, Custody status, Background and Benefits</div>
                </div>
              </label>
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-2 border-t border-slate-100">
            <Button variant="ghost" onClick={() => setShowPrefillModal(false)} className="text-xs rounded-xl text-slate-500 hover:bg-slate-50">
              Cancel
            </Button>
            <Button 
              onClick={handlePrefillOtherForms} 
              className="bg-red-600 hover:bg-red-700 text-white text-xs font-bold rounded-xl px-5 flex items-center gap-1.5"
            >
              <span>Pre-fill &amp; Open Selected</span>
              <ExternalLink className="w-3.5 h-3.5" />
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      </div> {/* Closing the print:hidden wrapper */}

      {/* Print-Only Layout matching vals_extracted.txt */}
      <div className="hidden print:block text-black font-serif text-[11px] leading-normal w-full max-w-4xl mx-auto p-2">
        <style dangerouslySetInnerHTML={{ __html: `
          @media print {
            body {
              background: white !important;
              color: black !important;
              font-family: Georgia, serif !important;
              font-size: 11px !important;
            }
            @page {
              size: A4;
              margin: 1.2cm 1.5cm;
            }
            .print-section {
              page-break-inside: avoid;
              margin-bottom: 12px;
            }
            .grid-cols-2 {
              display: grid;
              grid-template-columns: 1fr 1fr;
              gap: 12px;
            }
            .grid-cols-3 {
              display: grid;
              grid-template-columns: 1fr 1fr 1fr;
              gap: 12px;
            }
            .border-box {
              border: 1px solid #000;
              padding: 4px 6px;
              margin-top: 2px;
              min-height: 18px;
            }
            .underlined-value {
              border-bottom: 1px solid #777;
              padding-bottom: 1px;
              display: inline-block;
              min-width: 80px;
            }
          }
        `}} />

        {/* Section 1: Header */}
        <div className="text-center font-bold text-lg uppercase mb-4 tracking-wider border-b-2 border-black pb-1">
          {orgShort} Criminal Intake Form
        </div>
        
        <div className="grid-cols-2 print-section">
          <div>
            <div className="mb-2"><strong>DB Entry Date:</strong> <span className="underlined-value">{formatPrintDate(formData.db_entry_date)}</span></div>
            <div className="text-slate-700 italic text-[9px] mb-2">To be used in all Criminal Law Matters</div>
          </div>
          <div>
            <div className="mb-2"><strong>Entered By: (CrimSec)</strong> <span className="underlined-value">{formData.entered_by || "     "}</span></div>
            <div><strong>Form Completed By:</strong> <span className="underlined-value">{formData.form_completed_by || "     "}</span></div>
          </div>
        </div>

        {/* Section 2: Key Client Details */}
        <div className="print-section">
          <div className="bg-black text-white font-bold px-2 py-0.5 uppercase mb-2 tracking-wide text-xs">Key Client Details</div>
          
          <div className="grid-cols-3 mb-2">
            <div><strong>First Name:</strong> <div className="border-box">{formData.first_name || "     "}</div></div>
            <div><strong>Middle Name:</strong> <div className="border-box">{formData.middle_name || "     "}</div></div>
            <div><strong>Surname:</strong> <div className="border-box">{formData.surname || "     "}</div></div>
          </div>

          <div className="grid-cols-3 mb-2">
            <div><strong>Other Known Name(s):</strong> <div className="border-box">{formData.other_known_names || "     "}</div></div>
            <div><strong>DOB:</strong> <div className="border-box">{formatPrintDate(formData.dob)}</div></div>
            <div>
              <strong>Gender:</strong> 
              <div className="border-box">
                {formData.gender || "     "}
              </div>
            </div>
          </div>

          <div className="grid-cols-3 mb-2">
            <div>
              <strong>Custody:</strong> 
              <div className="mt-1">
                {renderPrintCheckbox(formData.in_custody === "Yes")} Yes &nbsp;&nbsp;&nbsp;&nbsp;
                {renderPrintCheckbox(formData.in_custody === "No")} No
              </div>
            </div>
            <div><strong>Location:</strong> <div className="border-box">{formData.custody_location || "     "}</div></div>
            <div><strong>CRN:</strong> <div className="border-box">{formData.crn || "     "}</div></div>
          </div>

          <div className="grid-cols-2 mb-2">
            <div><strong>Phone Number(s):</strong> <div className="border-box">{formData.phone || "     "}</div></div>
            <div><strong>Email:</strong> <div className="border-box">{formData.email || "     "}</div></div>
          </div>

          <div className="grid-cols-3 mb-2">
            <div className="col-span-2"><strong>Residential Address:</strong> <div className="border-box">{formData.res_address || "     "}</div></div>
            <div><strong>Suburb / Postcode:</strong> <div className="border-box">{formData.res_suburb || "     "} {formData.res_postcode ? `(${formData.res_postcode})` : ""}</div></div>
          </div>

          <div className="grid-cols-3 mb-2">
            <div className="col-span-2"><strong>Postal Address:</strong> <div className="border-box">{formData.postal_address || "     "}</div></div>
            <div><strong>Suburb / Postcode:</strong> <div className="border-box">{formData.postal_suburb || "     "} {formData.postal_postcode ? `(${formData.postal_postcode})` : ""}</div></div>
          </div>

          <div className="grid-cols-3 mb-2">
            <div>
              <strong>Homeless:</strong>
              <div className="mt-1">
                {renderPrintCheckbox(formData.homeless_status === "Yes")} Yes &nbsp;&nbsp;
                {renderPrintCheckbox(formData.homeless_status === "No")} No &nbsp;&nbsp;
                {renderPrintCheckbox(formData.homeless_status === "At Risk")} At Risk
              </div>
            </div>
            <div>
              <strong>Literate:</strong>
              <div className="mt-1">
                {renderPrintCheckbox(formData.literacy_status === "Well")} Well &nbsp;&nbsp;
                {renderPrintCheckbox(formData.literacy_status === "Not Very Well")} Not &nbsp;&nbsp;
                {renderPrintCheckbox(formData.literacy_status === "Not At All")} Not At All
              </div>
            </div>
            <div>
              <strong>Client is:</strong>
              <div className="mt-1">
                {renderPrintCheckbox(formData.indigenous_identity === "Aboriginal")} Aboriginal &nbsp;&nbsp;
                {renderPrintCheckbox(formData.indigenous_identity === "Torres Strait Islander")} TSI &nbsp;&nbsp;
                {renderPrintCheckbox(formData.indigenous_identity === "Both")} Both
              </div>
            </div>
          </div>

          <div className="grid-cols-3 mb-2">
            <div><strong>Authorised Person Name:</strong> <div className="border-box">{formData.auth_person_name || "     "}</div></div>
            <div><strong>Relationship:</strong> <div className="border-box">{formData.auth_relationship || "     "}</div></div>
            <div><strong>Phone Number:</strong> <div className="border-box">{formData.auth_phone || "     "}</div></div>
          </div>

          <div className="grid-cols-3 mb-2">
            <div>
              <strong>COA on Database:</strong>
              <div className="mt-1">
                {renderPrintCheckbox(formData.coa_on_db === "Yes")} Yes &nbsp;&nbsp;
                {renderPrintCheckbox(formData.coa_on_db === "No")} No
              </div>
            </div>
            <div><strong>Location on DB:</strong> <div className="border-box">{formData.coa_location_db || "     "}</div></div>
            <div>
              <strong>COA Cert Attached:</strong>
              <div className="mt-1">
                {renderPrintCheckbox(formData.coa_cert_attached === "Yes")} Yes &nbsp;&nbsp;
                {renderPrintCheckbox(formData.coa_cert_attached === "No")} No
              </div>
            </div>
          </div>
        </div>

        {/* Section 3: Family Lineage (Internal COA) */}
        <div className="print-section">
          <div className="bg-black text-white font-bold px-2 py-0.5 uppercase mb-2 tracking-wide text-xs">Internal COA</div>
          <div className="grid-cols-2 mb-2">
            <div><strong>Mum:</strong> <div className="border-box">{formData.mum_name || "     "}</div></div>
            <div><strong>Dad:</strong> <div className="border-box">{formData.dad_name || "     "}</div></div>
          </div>
          <div className="grid-cols-2 mb-2">
            <div><strong>Grandmother (Maternal):</strong> <div className="border-box">{formData.maternal_grandma || "     "}</div></div>
            <div><strong>Grandfather (Maternal):</strong> <div className="border-box">{formData.maternal_grandpa || "     "}</div></div>
          </div>
          <div className="grid-cols-2 mb-2">
            <div><strong>Grandmother (Paternal):</strong> <div className="border-box">{formData.paternal_grandma || "     "}</div></div>
            <div><strong>Grandfather (Paternal):</strong> <div className="border-box">{formData.paternal_grandpa || "     "}</div></div>
          </div>
          <div className="grid-cols-3 mb-2">
            <div><strong>Mob:</strong> <div className="border-box">{formData.mob_name || "     "}</div></div>
            <div><strong>ACCOs:</strong> <div className="border-box">{formData.accos_involved || "     "}</div></div>
            <div><strong>Other Family Members:</strong> <div className="border-box">{formData.other_family_members || "     "}</div></div>
          </div>
        </div>

        {/* Section 4: Health Factors */}
        <div className="print-section" style={{ pageBreakBefore: "always" }}>
          <div className="bg-black text-white font-bold px-2 py-0.5 uppercase mb-2 tracking-wide text-xs">Health Factors</div>
          <div className="grid-cols-3 mb-2">
            <div><strong>Physical:</strong> <div className="border-box" style={{ minHeight: "36px" }}>{formData.health_physical || "     "}</div></div>
            <div><strong>Intellectual:</strong> <div className="border-box" style={{ minHeight: "36px" }}>{formData.health_intellectual || "     "}</div></div>
            <div><strong>Mental Health:</strong> <div className="border-box" style={{ minHeight: "36px" }}>{formData.health_mental || "     "}</div></div>
          </div>
        </div>

        {/* Section 5: Financial */}
        <div className="print-section">
          <div className="bg-black text-white font-bold px-2 py-0.5 uppercase mb-2 tracking-wide text-xs">Financial</div>
          <div className="grid-cols-3 mb-2">
            <div><strong>Income:</strong> <div className="border-box">{formData.income_level || "     "}</div></div>
            <div><strong>Centrelink Benefit:</strong> <div className="border-box">{formData.centrelink_benefit || "     "}</div></div>
            <div><strong>CRN:</strong> <div className="border-box">{formData.financial_crn || "     "}</div></div>
          </div>
        </div>

        {/* Section 6: Court Details */}
        <div className="print-section">
          <div className="bg-black text-white font-bold px-2 py-0.5 uppercase mb-2 tracking-wide text-xs">Court Details</div>
          
          <div className="grid-cols-3 mb-2">
            <div><strong>Court Date:</strong> <div className="border-box">{formatPrintDate(formData.court_date)}</div></div>
            <div>
              <strong>Court Type:</strong>
              <div className="mt-1">
                {renderPrintCheckbox(formData.court_type === "Children's")} Children's &nbsp;
                {renderPrintCheckbox(formData.court_type === "Magistrates'")} Magistrates' &nbsp;
                {renderPrintCheckbox(formData.court_type === "County")} County &nbsp;
                {renderPrintCheckbox(formData.court_type === "Supreme")} Supreme
              </div>
            </div>
            <div><strong>Court Location:</strong> <div className="border-box">{formData.court_location || "     "}</div></div>
          </div>

          <div className="grid-cols-3 mb-2">
            <div><strong>Hearing Type:</strong> <div className="border-box">{formData.hearing_type || "     "}</div></div>
            <div>
              <strong>Adjourned Previously?</strong>
              <div className="mt-1">
                {renderPrintCheckbox(formData.adjourned_previously === "Yes")} Yes &nbsp;&nbsp;
                {renderPrintCheckbox(formData.adjourned_previously === "No")} No
              </div>
            </div>
            <div><strong>If Yes, how many times?</strong> <div className="border-box">{formData.adjourned_times || "     "}</div></div>
          </div>

          <div className="grid-cols-3 mb-2">
            <div>
              <strong>Client is on:</strong>
              <div className="mt-1">
                {renderPrintCheckbox(formData.client_on_status === "Summons")} Summons &nbsp;
                {renderPrintCheckbox(formData.client_on_status === "Bail")} Bail &nbsp;
                {renderPrintCheckbox(formData.client_on_status === "Remand")} Remand
              </div>
            </div>
            <div>
              <strong>Adjournment Required?</strong>
              <div className="text-[9px] text-slate-500 italic mt-0.5">Is court date a {orgShort} date?</div>
              <div className="mt-1">
                {renderPrintCheckbox(formData.vals_date === "Yes")} Yes &nbsp;&nbsp;
                {renderPrintCheckbox(formData.vals_date === "No")} No
              </div>
            </div>
            <div>
              <strong>Consent from client to adjourn?</strong>
              <div className="mt-1">
                {renderPrintCheckbox(formData.consent_to_adjourn === "Yes")} Yes &nbsp;&nbsp;
                {renderPrintCheckbox(formData.consent_to_adjourn === "No")} No
              </div>
            </div>
          </div>

          <div className="grid-cols-2 mb-2">
            <div>
              <strong>Have we sent an adjournment off? (Attach copy)</strong>
              <div className="mt-1">
                {renderPrintCheckbox(formData.sent_adjournment === "Yes")} Yes &nbsp;&nbsp;
                {renderPrintCheckbox(formData.sent_adjournment === "No")} No &nbsp;&nbsp;
                {renderPrintCheckbox(formData.sent_adjournment === "N/A")} N/A
              </div>
            </div>
            <div>
              <strong>Other Notes:</strong>
              <div className="border-box" style={{ minHeight: "36px" }}>{formData.court_notes || "     "}</div>
            </div>
          </div>
        </div>

        {/* Section 7: Charges */}
        <div className="print-section">
          <div className="bg-black text-white font-bold px-2 py-0.5 uppercase mb-2 tracking-wide text-xs">Charges</div>
          {Array.from({ length: 3 }).map((_, i) => {
            const charge = formData.charges_list?.[i] || {};
            return (
              <div key={i} className="border-b border-black pb-2 mb-2 last:border-b-0">
                <div className="grid-cols-2 mb-1">
                  <div><strong>Charges:</strong> <span className="underlined-value" style={{ width: "80%" }}>{charge.charge_title || withoutOffenceCode(charge.offence_group) || "     "}</span></div>
                  <div><strong>Informant:</strong> <span className="underlined-value" style={{ width: "80%" }}>{charge.informant || "     "}</span></div>
                </div>
                <div className="grid-cols-2">
                  <div><strong>VP #:</strong> <span className="underlined-value" style={{ width: "80%" }}>{charge.vp_number || "     "}</span></div>
                  <div><strong>Station:</strong> <span className="underlined-value" style={{ width: "80%" }}>{charge.station || "     "}</span></div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Section 8: Other Party / Co-Accused */}
        <div className="print-section">
          <div className="bg-black text-white font-bold px-2 py-0.5 uppercase mb-2 tracking-wide text-xs">Other Party / Co-Accused Name</div>
          
          <div className="grid-cols-3 mb-1 font-bold text-[9px] border-b border-black pb-1">
            <div>Co-Accused Name</div>
            <div>DOB</div>
            <div>Notes</div>
          </div>

          {/* Slot 1 from formData if available */}
          <div className="grid-cols-3 mb-2 border-b border-dashed border-slate-300 pb-1">
            <div style={{ minHeight: "14px" }}>{formData.co_accused_name || "     "}</div>
            <div>{formData.co_accused_dob ? formatPrintDate(formData.co_accused_dob) : "      /       /      "}</div>
            <div>{formData.co_accused_notes || "     "}</div>
          </div>

          {/* Extra slots to match template */}
          {Array.from({ length: 4 }).map((_, idx) => (
            <div key={idx} className="grid-cols-3 mb-2 border-b border-dashed border-slate-300 pb-1 last:border-0">
              <div style={{ minHeight: "14px" }}>     </div>
              <div>      /       /      </div>
              <div>     </div>
            </div>
          ))}
        </div>

        {/* Section 9: Conflicts */}
        <div className="print-section" style={{ pageBreakBefore: "always" }}>
          <div className="bg-black text-white font-bold px-2 py-0.5 uppercase mb-2 tracking-wide text-xs">Conflicts</div>
          <div className="grid-cols-2 mb-2">
            <div><strong>Conflict Check Completed By:</strong> <div className="border-box">{formData.conflict_checked_by || "     "}</div></div>
            <div><strong>Date Completed:</strong> <div className="border-box">{formatPrintDate(formData.conflict_check_date)}</div></div>
          </div>
          <div className="grid-cols-3 mb-2">
            <div>
              <strong>Conflict:</strong>
              <div className="mt-1">
                {renderPrintCheckbox(formData.conflict_status === "Yes")} Yes &nbsp;&nbsp;
                {renderPrintCheckbox(formData.conflict_status === "No")} No &nbsp;&nbsp;
                {renderPrintCheckbox(formData.conflict_status === "N/A")} N/A
              </div>
            </div>
            <div className="col-span-2"><strong>If N/A, Reason:</strong> <div className="border-box">{formData.conflict_na_reason || "     "}</div></div>
          </div>
          <div className="mb-2">
            <strong>If Yes, details of Conflict:</strong>
            <div className="border-box" style={{ minHeight: "36px" }}>{formData.conflict_details || "     "}</div>
          </div>
        </div>

        {/* Section 10: Checks */}
        <div className="print-section">
          <div className="bg-black text-white font-bold px-2 py-0.5 uppercase mb-2 tracking-wide text-xs">Checks</div>
          <div className="grid-cols-2 mb-2">
            <div>
              <strong>Have you done a client authority? (Must be done every time)</strong>
              <div className="mt-1">
                {renderPrintCheckbox(formData.client_authority_done === "Yes")} Yes &nbsp;&nbsp;
                {renderPrintCheckbox(formData.client_authority_done === "No")} No
              </div>
            </div>
            <div>
              <strong>Does the client already have a lawyer?</strong>
              <div className="mt-1">
                {renderPrintCheckbox(formData.already_has_lawyer === "Yes")} Yes &nbsp;&nbsp;
                {renderPrintCheckbox(formData.already_has_lawyer === "No")} No
              </div>
            </div>
          </div>
          <div className="grid-cols-2 mb-2">
            <div><strong>If Yes, Name of Lawyer:</strong> <div className="border-box">{formData.existing_lawyer_name || "     "}</div></div>
            <div>
              <strong>* If in custody * Has the client already applied for bail?</strong>
              <div className="mt-1">
                {renderPrintCheckbox(formData.applied_for_bail === "Yes")} Yes &nbsp;&nbsp;
                {renderPrintCheckbox(formData.applied_for_bail === "No")} No &nbsp;&nbsp;
                {renderPrintCheckbox(formData.applied_for_bail === "N/A")} N/A
              </div>
            </div>
          </div>
          <div className="mb-2">
            <strong>Does the client consent to a referral in the event that we cannot assist?</strong>
            <div className="mt-1">
              {renderPrintCheckbox(formData.consent_to_referral === "Yes")} Yes &nbsp;&nbsp;
              {renderPrintCheckbox(formData.consent_to_referral === "No")} No &nbsp;&nbsp;
              {renderPrintCheckbox(formData.consent_to_referral === "N/A")} N/A
            </div>
          </div>
        </div>

        {/* Section 11: Advice / Instructions */}
        <div className="print-section">
          <div className="bg-black text-white font-bold px-2 py-0.5 uppercase mb-2 tracking-wide text-xs">Advice / Instructions</div>
          <div className="border-box text-justify" style={{ minHeight: "80px", whiteSpace: "pre-wrap" }}>
            {formData.advice_instructions || "     "}
          </div>
        </div>

        {/* Section 12: To Do List */}
        <div className="print-section">
          <div className="bg-black text-white font-bold px-2 py-0.5 uppercase mb-2 tracking-wide text-xs">To Do List</div>
          <table className="w-full text-left text-[10px] border-collapse">
            <thead>
              <tr className="border-b border-black">
                <th className="py-1" style={{ width: "65%" }}>Item</th>
                <th className="py-1" style={{ width: "15%" }}>Completed?</th>
                <th className="py-1" style={{ width: "20%" }}>Completed By</th>
              </tr>
            </thead>
            <tbody>
              {Array.from({ length: 5 }).map((_, idx) => {
                const item = formData.checklist?.[idx] || {};
                return (
                  <tr key={idx} className="border-b border-dotted border-slate-300">
                    <td className="py-1">{item.item || "     "}</td>
                    <td className="py-1">
                      {renderPrintCheckbox(item.completed === "Yes")} Yes &nbsp;
                      {renderPrintCheckbox(item.completed === "No")} No
                    </td>
                    <td className="py-1">{item.completed_by || "     "}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Section 13: Any Other Notes */}
        <div className="print-section">
          <div className="bg-black text-white font-bold px-2 py-0.5 uppercase mb-2 tracking-wide text-xs">Any Other Notes</div>
          <div className="border-box" style={{ minHeight: "40px", whiteSpace: "pre-wrap" }}>
            {formData.other_notes || "     "}
          </div>
        </div>

        {/* Section 14: Sign-Off */}
        <div className="print-section">
          <div className="bg-black text-white font-bold px-2 py-0.5 uppercase mb-2 tracking-wide text-xs">Sign-Off</div>
          <div className="grid-cols-2 mt-2">
            <div>
              <strong>Solicitor / Paralegal Signature:</strong>
              <div className="border-b border-black mt-4 h-5 w-3/4"></div>
            </div>
            <div>
              <strong>Date:</strong>
              <div className="mt-4"><span className="underlined-value">{formatPrintDate(formData.sig_date)}</span></div>
            </div>
          </div>
        </div>

      </div>

    </div>
  );
}

// Print specific helpers
function formatPrintDate(dateStr) {
  if (!dateStr) return "      /       /      ";
  try {
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return dateStr;
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day} / ${month} / ${year}`;
  } catch (e) {
    return dateStr;
  }
}

function renderPrintCheckbox(isChecked) {
  return (
    <span className="inline-block font-sans text-xs select-none">
      {isChecked ? "☒" : "☐"}
    </span>
  );
}

